import React from 'react';
import { Card, Col, OverlayTrigger, Popover, Row, Tab, Table, Tabs, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Ethnicity,
  Menopause,
  PanelField,
  FurtherConsiderations,
  PanelMarker,
  Section,
  practitionerSectionStateKey,
  practitionerSectionTitle,
} from '../../models';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { validate, ValidationError } from 'class-validator';
import { UserProfile } from '../../models/UserProfile';
import { resolveUnitSystem } from '../../models/units/resolveUnitSystem';
import { DisclaimerSection } from './disclaimer-section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { faFileDownload as faFileDownloadSolid, faArrowUp, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { BloodworkTestAnalysisSection } from './bloodwork-test-analysis-section';
import { makeWithPreviousOnly } from '../../helpers/previousOnlyMarkers';
import { SectionVideoIcon } from '../common/section-video-icon';
import { ImmuneInflammatoryBurdenPanel } from './immune-inflammatory-burden-panel';
import { MetabolicVulnerabilityInsights } from './metabolic-vulnerability-insights';
import { MvxResultsPanel } from './mvx-results-panel';
import { MvxBlueprintPanel } from './mvx-blueprint-panel';
import { MvxAboutSection } from './mvx-about-section';
import { MvxReferencesSection } from './mvx-references-section';
import { LABCORP_MVX_PLUS_URL } from '../../models/MetabolicVulnerability';
import { Footer } from '../../Footer';
import { RootCauseFrameworkPanel } from './root-cause-framework-panel';
import { RootCauseGuidanceTabs } from './root-cause-guidance-tabs';
import { FUNCTIONAL_MAP_LABEL, ResultsOverviewPanel, rankSectionsBySeverity, hasResultsOverviewContent, isHdlHighSoleOutOfOptimal, isGlobulinOptimal, isImmuneAbsolute, keepInInflammationPanel, keepInImmunePanel, suppressAlpWhenZincSupersedes } from './results-overview-panel';
import { SupplementsSummaryPanel } from './supplements-summary-panel';
import { supplementsVisible } from '../../utils/supplementsVisibility';
import { SupplementProtocolsPanel, hasSupplementProtocolsContent } from './supplement-protocols-panel';
import {
  HOLOBIOME_SECTION,
  HOLOBIOME_SYSTEMS_SECTION,
  HOLOBIOME_TOP_SECTION,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import {
  RecommendedSupplementsPanel,
  clientListSelection,
  readExcludedFromClientList,
  readSupplementEdits,
  writeExcludedFromClientList,
  writeSupplementEdits,
} from './recommended-supplements-panel';
import { SectionNotesSummaryPanel } from './section-notes-summary-panel';
import { GenericModal } from '../common/generic-modal';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodwork-notes';
import { SummaryDashboardSection } from './summary-dashboard-section';
import { buildSystemGroupDefs, buildAttrToSystem, buildSecondaryTagger, appendSecondaryLabels, groupBySystem as groupMarkersBySystem } from '../../models/mooSystemGrouping';
import { BMIBarChart } from '../charts/BMIBarChart';
import { Client, Gender, SupplementEdit } from '../../models';
import { MathUtils } from '../../utils';
import Select from 'react-select';
import { HeightOption, FT_OPTIONS, IN_OPTIONS, HEIGHT_SELECT_STYLES } from '../client/height-select-options';
import { visibleExceptProd } from '../../utils/featureVisibility';
import { digestionVisible } from '../../utils/digestionVisibility';

const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');

// Collapse / Include-in-PDF key for the dev-only MVX section. Cast to Section so
// it reuses the existing toggle machinery without polluting the Section enum.
const MVX_SECTION = 'Metabolic Vulnerability (MVX Plus)' as unknown as Section;

// The I³ report section is parked alongside everything else BWH Labs — it
// carries their byline, and the partnership isn't finalized. Flip this back to
// true to show it again (it stays env-gated to local dev on top of this flag).
// The matching entry card is already off: SHOW_IMMUNE_INFLAMMATORY_CARD in
// bloodwork-entry.tsx. See bwhLabsVisible() for the supplement-brand half.
const SHOW_IMMUNE_INFLAMMATORY_SECTION = false;

// The Systemic Formulas popover runs to five topics. As one unbroken column of
// paragraphs behind bold lead-ins, finding the one you came for meant reading
// the ones you didn't: every block opened the same way, at the same weight, in
// the same column. Real headings give it a spine you can skim.
//
// A heading, not a bold lead-in, which is the whole difference: it sits on its
// own line, in the report's near-black, above the text it governs, so the eye
// can run down the left edge and stop at the right one.
const POPOVER_HEADING: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: '14px',
  color: '#212529',
  marginTop: '14px',
  marginBottom: '3px',
};
const POPOVER_BODY: React.CSSProperties = { marginBottom: 0 };

type Props = {
  currentUserProfile: UserProfile;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
  updateClient?: (client: Client) => Promise<Client>;
  currentClients?: Client[];
};

// Keys for the 6 "Markers Outside Optimal" sub-panels — used to track
// which panel(s) the user has collapsed (charts hidden) for the current session.
type OutsideOptimalPanelKey =
  | 'highAlarm'
  | 'lowAlarm'
  | 'aboveLab'
  | 'belowLab'
  | 'aboveOptimal'
  | 'belowOptimal';

const ALL_OUTSIDE_OPTIMAL_PANEL_KEYS: OutsideOptimalPanelKey[] = [
  'highAlarm', 'lowAlarm', 'aboveLab', 'belowLab', 'aboveOptimal', 'belowOptimal',
];

type State = {
  previousBloodwork: Bloodwork;
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  showUpgradeModal: boolean;
  hideTestResultsAnalysis: boolean;
  collapsedSections: Section[];
  // Sections to exclude from the next PDF export. Seeded from the user profile
  // on mount and on bloodwork switch; toggled via the "Include in PDF" checkbox
  // in each section header. Session-only — never persists back to the profile.
  excludedFromPdfSections: Section[];
  editingClientInfo: boolean;
  pendingClient: Client | null;
  isSavingClientInfo: boolean;
  clientInfoValidationErrors: string[];
  // Per-session list of panels the user has collapsed (charts hidden).
  // Seeded on mount/bloodwork-switch from the user's
  // `markersOutsideOptimalDisplay` preference: 'showBars' → empty (all expanded),
  // 'hideBars' → all 6 keys (all collapsed). The user can flip any panel via its
  // chevron without persisting the change back to the profile.
  outsideOptimalChartsCollapsed: OutsideOptimalPanelKey[];
  // Session-only mirrors of the two Immune Section settings, exposed as quick
  // checkboxes in the Immune Activity section header so the user can flip them
  // without going back to Report Settings. Seeded on mount/bloodwork-switch from the
  // profile (`showOnlyImmuneSectionSignals`, `showImmuneAbsolutes`); toggling
  // them drives the on-screen Immune section (and its PDF export) but never
  // persists back to the profile — same pattern as "Include in PDF".
  showOnlyImmuneSectionSignals: boolean;
  showImmuneAbsolutes: boolean;
  // Products the practitioner has taken OFF the client's Recommended
  // Supplements list, normalized. Inverted on purpose — the list defaults to
  // everything this test recommends, so it is the removals that are worth
  // recording. Seeded from the test's own note in analyze(), and saved back to
  // it (debounced) on every tick, so it survives closing the report.
  excludedFromClientList: Set<string>;
  clientListSaveState?: 'saving' | 'saved' | 'error';
  // Products the practitioner added to the client list, and text they changed
  // on it. Shares the tick list's debounce: both live on the same test, and one
  // write carries both.
  supplementEdits: SupplementEdit[];
  // Height of the spacer under the report, grown by jumpToSectionNotes when the
  // report is too short for its target to reach the top of the viewport. See
  // that method for why.
  jumpSpacerHeight: number;
};

// Module scope, not instance state: this component remounts whenever the
// practitioner switches client or test, and an instance field would lose the
// handle to a popup that is still open — leaving an orphan window on screen for
// every test exported. Module scope survives those remounts, so each export
// still recycles a single window.
let pdfExportWindow: Window | null = null;

export class BloodworkTestAnalysis extends React.Component<Props, State> {
  state: State = {
    previousBloodwork: undefined,
    collectionDate: '',
    bloodworkAnalysis: undefined,
    showUpgradeModal: false,
    hideTestResultsAnalysis: false,
    collapsedSections: [],
    editingClientInfo: false,
    pendingClient: null,
    isSavingClientInfo: false,
    clientInfoValidationErrors: [],
    outsideOptimalChartsCollapsed: [],
    excludedFromPdfSections: [],
    showOnlyImmuneSectionSignals: true,
    showImmuneAbsolutes: true,
    excludedFromClientList: new Set<string>(),
    supplementEdits: [],
    jumpSpacerHeight: 0,
  };
  private previousBloodworkAnalysis?: BloodworkAnalysis;

  componentDidMount() {
    this.seedExcludedFromPdfFromProfile();
    this.seedOutsideOptimalCollapsedFromProfile();
    this.seedImmuneTogglesFromProfile();
    // Cross-section "See the … section" links inside Possible Root Causes text
    // (e.g. a viscosity input pointing at the HCT or protein section) fire this
    // window event instead of drilling a callback down through the tab tree.
    window.addEventListener(
      'labsmarts:navigate-section',
      this.handleSectionNav as EventListener,
    );
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.clearTimeout(this.clientListSavedNoticeTimer);
    window.removeEventListener(
      'labsmarts:navigate-section',
      this.handleSectionNav as EventListener,
    );
    // A tick that is still inside its debounce when the practitioner navigates
    // away would otherwise be lost — the one case where the delay that makes
    // six ticks one write could cost them their curation.
    this.flushClientListSave();
  }

  handleSectionNav = (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    if (!detail.section) return;
    this.navigateToSection(detail.section as Section, detail.tab === 'causes');
  };

  componentWillReceiveProps(nextProps: Props) {
    console.log('BloodworkTestAnalysis.componentWillReceiveProps......');
    console.log(nextProps);
    const { bloodwork, previousBloodwork } = nextProps;
    const prevBloodworkId = this.props.bloodwork?.id;
    const nextBloodworkId = bloodwork?.id;
    const prevPreviousId = this.props.previousBloodwork?.id;
    const nextPreviousId = previousBloodwork?.id;
    if (nextBloodworkId !== prevBloodworkId) {
      // Switching bloodworks resets the per-panel "show/hide bars" toggles and
      // the per-test "Include in PDF" overrides back to whatever the user's
      // Settings preferences say.
      this.seedOutsideOptimalCollapsedFromProfile(nextProps.currentUserProfile);
      this.seedExcludedFromPdfFromProfile(nextProps.currentUserProfile);
      this.seedImmuneTogglesFromProfile(nextProps.currentUserProfile);
    }
    // When the parent clears the selected bloodwork (e.g. user clicked Edit
    // and we want the panel to disappear), drop the cached analysis so
    // render() returns null instead of leaving the prior panel visible.
    if (!bloodwork) {
      this.setState({ bloodworkAnalysis: undefined, previousBloodwork: undefined });
      return;
    }
    // Only re-analyze when the user switched to a different current or
    // comparison test. Saving the same bloodwork (e.g. editing the
    // Supplements override or Insights notes) shouldn't rebuild the analysis
    // or scroll the page back to the top of the Test Results Analysis panel.
    const switched =
      nextBloodworkId !== prevBloodworkId ||
      nextPreviousId !== prevPreviousId;
    // Cover the home-screen race where the user clicks Analyze on the first
    // test before bloodworkRanges have finished loading: the first analyze()
    // call exits without building an analysis, and every later props update
    // carries the same bloodwork id, so `switched` stays false and the panel
    // would remain blank until the user picked a *different* test. As long as a
    // test is selected, ranges are loaded, and we have no analysis yet, build
    // it. This is idempotent — `needsInitialAnalysis` is false once an analysis
    // exists, so unrelated prop updates (profile saves, re-saving the same
    // bloodwork) never rebuild it.
    const nextRanges = nextProps.bloodworkRanges;
    const rangesReady = !!nextRanges && nextRanges.length > 0;
    const needsInitialAnalysis = !this.state.bloodworkAnalysis;
    if (switched || (rangesReady && needsInitialAnalysis)) {
      // BEFORE analyze(), which replaces excludedFromClientList with the
      // incoming test's. A tick still inside its 800ms debounce would otherwise
      // be written against the wrong test: the timer fires after the swap, so
      // it would save the NEW test's list and the practitioner's last tick on
      // the old one would be gone, having shown "Saving…" the whole time.
      this.flushClientListSave();
      // Pass nextProps' ranges — this.props.bloodworkRanges is still the stale
      // pre-update value while we're inside componentWillReceiveProps.
      this.analyze(
        bloodwork,
        previousBloodwork,
        nextRanges,
        nextProps.furtherConsiderations,
      );
    }
  }

  private seedExcludedFromPdfFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    this.setState({
      excludedFromPdfSections: [...(p?.excludedFromPdfSections ?? [])],
    });
  };

  // Seed the Markers Outside Optimal section from the user's display-mode
  // preference — both the section card itself and which of its 6 sub-panels
  // start with their charts hidden:
  //   showBars        - section open, no sub-panel collapsed (the default)
  //   hideBars        - section open, all 6 sub-panels collapsed
  //   hideSection     - skips rendering entirely, so neither list matters
  //   minimizeSection - section card collapsed to its header; opening it
  //                     reveals the 'showBars' layout
  // Only MARKERS_OUTSIDE_OPTIMAL is added to / removed from collapsedSections —
  // any other section the user has collapsed by hand is left alone.
  private seedOutsideOptimalCollapsedFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    const mode = p?.markersOutsideOptimalDisplay ?? 'showBars';
    this.setState(prev => ({
      outsideOptimalChartsCollapsed:
        mode === 'hideBars' ? [...ALL_OUTSIDE_OPTIMAL_PANEL_KEYS] : [],
      collapsedSections:
        mode === 'minimizeSection'
          ? (prev.collapsedSections.includes(Section.MARKERS_OUTSIDE_OPTIMAL)
              ? prev.collapsedSections
              : [...prev.collapsedSections, Section.MARKERS_OUTSIDE_OPTIMAL])
          : prev.collapsedSections.filter(
              s => s !== Section.MARKERS_OUTSIDE_OPTIMAL,
            ),
    }));
  };

  // Seed the two Immune Section quick toggles from the user's saved
  // preferences. Both default to true when unset (matching Report Settings).
  private seedImmuneTogglesFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    this.setState({
      showOnlyImmuneSectionSignals: p?.showOnlyImmuneSectionSignals ?? true,
      showImmuneAbsolutes: p?.showImmuneAbsolutes ?? true,
    });
  };

  toggleShowOnlyImmuneSectionSignals = () => {
    this.setState(prev => ({ showOnlyImmuneSectionSignals: !prev.showOnlyImmuneSectionSignals }));
  };

  toggleShowImmuneAbsolutes = () => {
    this.setState(prev => ({ showImmuneAbsolutes: !prev.showImmuneAbsolutes }));
  };

  // `section` is a Section for every shipped panel, and a composite key
  // (practitionerSectionStateKey) for a section the practitioner named
  // themselves — those all render as Section.CUSTOM_MARKERS and would otherwise
  // collapse and leave the PDF as one lump.
  toggleIncludeInPdf = (section: Section | string) => {
    this.setState(prev => {
      const excluded = prev.excludedFromPdfSections.includes(section as Section);
      return {
        excludedFromPdfSections: excluded
          ? prev.excludedFromPdfSections.filter(s => s !== section)
          : [...prev.excludedFromPdfSections, section as Section],
      };
    });
  };

  private isIncludedInPdf = (section: Section | string): boolean =>
    !this.state.excludedFromPdfSections.includes(section as Section);

  // ── The client's Recommended Supplements list ──
  //
  // Ticked in the by-system tables, read by the Recommended Supplements card
  // and its PDF block. Unlike "Include in PDF", this one PERSISTS: it is a
  // clinical decision about a particular test, not a per-export preference, and
  // a practitioner who curates a list, sends the report and reopens it a month
  // later should find their list intact.
  private clientListSaveTimer: number | undefined;

  // The unmount flush leaves a save in flight with nobody to report to: its
  // resolution lands after this component is gone, and setState there is a React
  // warning rather than an indicator anyone can read. A failure at that point
  // still reaches the console.
  private unmounted = false;

  // Clears the "Changes saved" line a few seconds after it appears. Without it
  // the card carries a status label permanently, which reads as a property of
  // the card rather than the result of something the practitioner just did —
  // and the first question it gets asked is "saved what?". Saving… and the
  // error state are NOT cleared: one is transient by nature and the other has
  // to stay until the write actually succeeds.
  private clientListSavedNoticeTimer: number | undefined;

  private showSavedNotice = () => {
    window.clearTimeout(this.clientListSavedNoticeTimer);
    this.clientListSavedNoticeTimer = window.setTimeout(() => {
      if (!this.unmounted) {
        this.setState(prev =>
          prev.clientListSaveState === 'saved'
            ? { clientListSaveState: undefined }
            : null,
        );
      }
    }, 4000);
  };

  toggleClientListProduct = (productName: string) => {
    const key = normalizeSupplementName(productName);
    this.setState(prev => {
      // A NEW Set, not a mutation: React compares by reference, and the tables
      // below only re-render if this changes identity.
      const excluded = new Set(prev.excludedFromClientList);
      if (excluded.has(key)) excluded.delete(key);
      else excluded.add(key);
      return { excludedFromClientList: excluded, clientListSaveState: 'saving' as const };
    }, this.scheduleClientListSave);
  };

  // The card's Edit mode: a product added or removed, or a row's text changed.
  // Goes through the same debounce as the ticks, so a practitioner who adds
  // three products and retypes a dose makes one write, and the header's
  // Saving / Saved indicator keeps meaning what it says.
  setSupplementEdits = (supplementEdits: SupplementEdit[]) => {
    this.setState(
      { supplementEdits, clientListSaveState: 'saving' as const },
      this.scheduleClientListSave,
    );
  };

  // Debounced, so ticking a system's worth of products is one write rather than
  // one per checkbox. Nothing else in this report autosaves, which is why the
  // card shows the state of the write rather than leaving it invisible.
  private scheduleClientListSave = () => {
    window.clearTimeout(this.clientListSaveTimer);
    this.clientListSaveTimer = window.setTimeout(this.saveClientList, 800);
  };

  private flushClientListSave = () => {
    if (this.clientListSaveTimer === undefined) return;
    window.clearTimeout(this.clientListSaveTimer);
    this.clientListSaveTimer = undefined;
    this.saveClientList();
  };

  private saveClientList = () => {
    this.clientListSaveTimer = undefined;
    const bloodwork = this.state.bloodworkAnalysis?.bloodwork;
    if (!bloodwork) return;
    writeExcludedFromClientList(bloodwork, this.state.excludedFromClientList);
    writeSupplementEdits(bloodwork, this.state.supplementEdits);
    this.props.bloodworkEntryStore
      // Silent: no global spinner. That modal locks body scrolling and drops
      // the report at the top, which for an autosave means clicking a product
      // in the picker throws the practitioner back to the top of the page.
      // This card reports its own save state a few lines under the button.
      .saveBloodwork(bloodwork, { silent: true })
      .then(() => {
        if (this.unmounted) return;
        this.setState({ clientListSaveState: 'saved' });
        this.showSavedNotice();
      })
      .catch(err => {
        console.error('Failed to save the client supplement list', err);
        if (!this.unmounted) this.setState({ clientListSaveState: 'error' });
      });
  };

  // `ranges` defaults to the current props, but callers inside
  // componentWillReceiveProps MUST pass nextProps.bloodworkRanges: at that
  // point this.props still holds the pre-update (often empty) ranges, so
  // reading them here would make the ranges-just-loaded recovery silently bail
  // and leave the analysis panel blank until the user picks a different test.
  analyze(
    bloodwork: Bloodwork,
    previousBloodwork: Bloodwork,
    ranges: BloodworkRange[] = this.props.bloodworkRanges,
    // Same reason `ranges` is a parameter: inside componentWillReceiveProps,
    // this.props is still the PREVIOUS render's value. The client list's
    // default is derived from the considerations (an uncurated test starts at
    // the Top Priority shortlist), and deriving it from a stale — possibly
    // empty — considerations set silently produces "everything ticked" and
    // never recomputes, because the note is only read on analyze.
    considerations: FurtherConsiderations = this.props.furtherConsiderations,
  ) {
    const bloodworkRanges = ranges;
    if (bloodwork && bloodworkRanges && bloodworkRanges.length > 0) {
      const defaultUnitSystem = this.props.currentUserProfile?.unitSystem;
      // Text markers resolve their own previous-only entries inside the
      // analysis, so this setting has to travel with it — the numeric side
      // applies the same setting through makeWithPreviousOnly in render().
      const showPreviousOnlyMarkers =
        this.props.currentUserProfile?.showPreviousTestMarkersInComparison ?? true;
      const bloodworkAnalysis = new BloodworkAnalysis({
        bloodwork,
        bloodworkRanges,
        bloodworkFields: PanelField.ALL_FIELDS,
        defaultUnitSystem,
        showPreviousOnlyMarkers,
      });
      bloodworkAnalysis.init().performAnalysis();

      if (previousBloodwork) {
        const previousBloodworkAnalysis = new BloodworkAnalysis({
          bloodwork: previousBloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
          defaultUnitSystem,
        });
        previousBloodworkAnalysis.init();
        bloodworkAnalysis.preparePreviousPanelMarkers(
          previousBloodworkAnalysis,
        );
        this.previousBloodworkAnalysis = previousBloodworkAnalysis;
      } else {
        this.previousBloodworkAnalysis = undefined;
      }

      this.setState(
        {
          bloodworkAnalysis,
          previousBloodwork: previousBloodwork
            ? new Bloodwork(previousBloodwork)
            : undefined,
          // Read the curated client list off the test being opened, not the one
          // being left: this is the only place a switch of test is known.
          //
          // The models go in because an UNCURATED test has no note to read: the
          // list then starts at the Top Priority shortlist, which has to be
          // computed from this test's own patterns. See
          // defaultExcludedFromClientList.
          excludedFromClientList: readExcludedFromClientList(
            bloodworkAnalysis,
            considerations,
            this.props.currentUserProfile?.enabledSupplementBrands,
          ),
          supplementEdits: readSupplementEdits(bloodworkAnalysis),
          clientListSaveState: undefined,
        },
        // Scroll only after the panel has been committed to the DOM. Calling it
        // synchronously (as before) ran before the first render committed
        // #test-results-analysis-panel, leaving the scroll to a fragile 1s
        // setTimeout fallback — the panel would render but not come into view
        // on the first analyze.
        () => this.scrollToAnalyzeTestResults(),
      );
    }
  }

  startEditClientInfo = (): void => {
    const bloodwork = this.state.bloodworkAnalysis?.bloodwork;
    const client = bloodwork?.client;
    if (!client) return;
    const pending = new Client(client);
    // Default height/weight/units from the live client record when missing
    // on the snapshot — covers tests saved before height/weight were added
    // to the client profile. Fall back to bloodwork.clientId since older
    // snapshots may not carry id on the embedded client.
    const liveClientId = client.id ?? bloodwork?.clientId;
    const liveClient = liveClientId && this.props.currentClients
      ? Client.findClient(liveClientId, this.props.currentClients)
      : undefined;
    if (liveClient) {
      if (pending.height == null && liveClient.height != null) {
        pending.height = liveClient.height;
      }
      if (pending.weight == null && liveClient.weight != null) {
        pending.weight = liveClient.weight;
      }
      if (pending.units == null && liveClient.units != null) {
        pending.units = liveClient.units;
      }
    }
    this.setState({
      editingClientInfo: true,
      pendingClient: pending,
      clientInfoValidationErrors: [],
    });
  };

  cancelEditClientInfo = (): void => {
    this.setState({
      editingClientInfo: false,
      pendingClient: null,
      clientInfoValidationErrors: [],
    });
  };

  patchPendingClient = (apply: (client: Client) => void): void => {
    const { pendingClient } = this.state;
    if (!pendingClient) return;
    apply(pendingClient);
    this.forceUpdate();
  };

  private collectValidationMessages(
    pending: Client,
    classValidatorErrors: ValidationError[],
  ): string[] {
    const messages: string[] = [];
    const labels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      gender: 'Birth Gender',
      dateOfBirth: 'Date of Birth',
      ethnicity: 'Ethnicity',
      menopause: 'Menopause/Pregnant',
      trimester: 'Trimester',
      elevation: 'Elevation',
      cigaretteSmoker: 'Cigarette Smoker',
      alcoholDrinker: 'Alcohol Drinker',
      height: 'Height',
      weight: 'Weight',
    };
    classValidatorErrors.forEach((err) => {
      const label = labels[err.property] ?? err.property;
      Object.keys(err.constraints || {}).forEach((c) => {
        messages.push(`${label} ${err.constraints[c]}`);
      });
    });
    if (pending.isTooYoungToBePregnant?.()) {
      messages.push('Date of Birth — client must be 15 years of age or older when Pregnant is selected.');
    }
    if (pending.isTooOld?.()) {
      messages.push('Date of Birth — client cannot be over 200 years of age.');
    }
    return messages;
  }

  saveClientInfo = async (): Promise<void> => {
    const { pendingClient, bloodworkAnalysis, previousBloodwork } = this.state;
    const bloodwork = bloodworkAnalysis?.bloodwork;
    if (!pendingClient || !bloodwork) return;

    this.setState({ isSavingClientInfo: true, clientInfoValidationErrors: [] });

    // Client-side validation first
    const classErrors = await validate(pendingClient);
    const messages = this.collectValidationMessages(pendingClient, classErrors);
    if (messages.length > 0) {
      this.setState({ clientInfoValidationErrors: messages, isSavingClientInfo: false });
      return;
    }

    const currentClient = bloodwork.client;
    const editableKeys: (keyof Client)[] = [
      'firstName', 'lastName', 'gender', 'dateOfBirth', 'ethnicity',
      'menopause', 'trimester', 'elevation', 'cigaretteSmoker', 'alcoholDrinker',
      'height', 'weight', 'units',
    ];
    try {
      editableKeys.forEach((k) => { (currentClient as any)[k] = (pendingClient as any)[k]; });
      // Persist height/weight on the test record itself — not only on the
      // embedded client snapshot. These are the canonical per-test fields the
      // data-entry path writes (bloodwork-entry.tsx) and that the BMI read
      // falls back to (`eff.height ?? bloodwork.height`). Storing them here
      // keeps this test's BMI even if the client profile's height/weight later
      // change or the embedded snapshot is re-derived. (Both are numbers in
      // canonical units: height in cm, weight in lbs.)
      bloodwork.height = pendingClient.height;
      bloodwork.weight = pendingClient.weight;
      if (this.props.updateClient) {
        await this.props.updateClient(currentClient);
      }
      // Also re-save the bloodwork so its embedded client snapshot persists
      // (otherwise the next load would show stale client values for this test).
      const savedBloodwork = await this.props.bloodworkEntryStore.saveBloodwork(bloodwork);
      if (!savedBloodwork) {
        throw new Error('Test results failed to save — please check your connection and try again.');
      }
      // Re-sync the in-memory client test list from the server. The analysis
      // works on a *clone* of the list entry (BloodworkAnalysis copies the
      // bloodwork via `new Bloodwork(...)`), so the entry the table re-selects
      // stays stale until reloaded. Without this, switching to another test and
      // back would drop the just-saved height/weight/BMI even though they were
      // persisted. Mirrors the data-entry path (bloodwork-entry.tsx).
      if (bloodwork.clientId && this.props.bloodworkEntryStore.loadClientBloodworks) {
        this.props.bloodworkEntryStore
          .loadClientBloodworks(bloodwork.clientId)
          .catch((e) => console.error('Failed to refresh client bloodworks after saving client info', e));
      }
      // Re-run analysis since reference ranges depend on age/sex/etc.
      this.analyze(bloodwork, previousBloodwork);
      this.setState({
        editingClientInfo: false,
        pendingClient: null,
        clientInfoValidationErrors: [],
      });
    } catch (err) {
      console.error('Failed to save client info', err);
      const detail = (err && (err.response?.data?.message || err.message || JSON.stringify(err))) || 'Unknown error';
      this.setState({
        clientInfoValidationErrors: [`Server error: ${detail}`],
      });
    } finally {
      this.setState({ isSavingClientInfo: false });
    }
  };

  scrollToAnalyzeTestResults() {
    const scrollToPanel = () => {
      const element = document.getElementById('test-results-analysis-panel');
      if (!element) return false;
      // Offset by the sticky header height so the panel title lands at the
      // very top of the visible page instead of hiding behind the navbar.
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
      return true;
    };
    if (!scrollToPanel()) {
      // Hack to resolve issue with scrolling down the first time.
      setTimeout(scrollToPanel, 1000);
    }
  }

  export = (withNotes = false) => {
    console.log('export button clicked.....');
    const { currentUserProfile } = this.props;
    const { bloodworkAnalysis, previousBloodwork, excludedFromPdfSections, outsideOptimalChartsCollapsed } = this.state;

    if (!currentUserProfile.pdfExportAllowed) {
      this.setState({ showUpgradeModal: true });
      return;
    }

    // Write any pending client-list tick into the bloodwork BEFORE it is
    // serialized. The export hands the PDF window this object, and the list is
    // stored on it as a note — so a practitioner who ticks a product and hits
    // Export inside the save debounce would otherwise print the previous list.
    this.flushClientListSave();

    sessionStorage.setItem(
      'bloodwork',
      JSON.stringify(bloodworkAnalysis.bloodwork),
    );
    if (previousBloodwork) {
      sessionStorage.setItem(
        'previousBloodwork',
        JSON.stringify(previousBloodwork),
      );
    } else {
      sessionStorage.removeItem('previousBloodwork');
    }
    if (withNotes) {
      sessionStorage.setItem('exportWithNotes', 'true');
    } else {
      sessionStorage.removeItem('exportWithNotes');
    }
    // PDF inclusion: a section is excluded iff its "Include in PDF" checkbox
    // is unchecked at click time. UI collapse state no longer affects PDF.
    sessionStorage.setItem(
      'excludedFromPdfSections',
      JSON.stringify(excludedFromPdfSections),
    );
    // Mirror the on-screen "Show bar graphs" checkbox into the PDF: if any
    // MOO sub-panel is collapsed at export time, the PDF renders without bars.
    // Lets the user flip bars on/off per-export without touching the saved
    // `markersOutsideOptimalDisplay` profile preference.
    sessionStorage.setItem(
      'showBarsInMoo',
      JSON.stringify(outsideOptimalChartsCollapsed.length === 0),
    );
    sessionStorage.setItem(
      'showOnlySectionSignals',
      JSON.stringify(!!currentUserProfile.showOnlySectionSignals),
    );
    // Mirror the Immune Activity section's on-screen quick toggles into the
    // PDF so the export matches what the user is looking at, without touching
    // the saved profile preferences.
    sessionStorage.setItem(
      'showOnlyImmuneSectionSignals',
      JSON.stringify(this.state.showOnlyImmuneSectionSignals),
    );
    sessionStorage.setItem(
      'showImmuneAbsolutes',
      JSON.stringify(this.state.showImmuneAbsolutes),
    );
    sessionStorage.setItem(
      'showPreviousTestMarkersInComparison',
      JSON.stringify(currentUserProfile.showPreviousTestMarkersInComparison ?? true),
    );
    // Resolved here rather than re-derived in the PDF window: that window loads
    // the bloodwork but not the practitioner profile, so it cannot see the
    // Settings default on its own. Without this the screen prints mmol/L while
    // the PDF prints mg/dL for the same test.
    sessionStorage.setItem(
      'unitSystem',
      resolveUnitSystem({
        client: this.props.bloodwork?.client,
        profile: currentUserProfile,
      }),
    );

    if (pdfExportWindow && !pdfExportWindow.closed) {
      pdfExportWindow.close();
    }
    // A UNIQUE window name per export, never a fixed one. window.open reuses any
    // window already carrying the name and merely navigates it — and the browser
    // copies sessionStorage from the opener only when a browsing context is
    // CREATED, not when an existing one is navigated. A reused window therefore
    // keeps its own store, which the PDF view wipes on mount, so every key
    // written above reads back null there and the report renders from an empty
    // Bloodwork. The close() above handles the common case, but it can only
    // reach windows this tab still holds a handle to — reload the tab, or open
    // the app in a second one, and a stale window is out of reach while its
    // name lives on. A fresh name always creates a fresh context, so the
    // payload arrives no matter what is still open. The popup feature string is
    // likewise honoured only on creation, so sizing stays correct for the same
    // reason.
    pdfExportWindow = window.open(
      '/bloodwork/analyze/pdf',
      `labsmartsPdf_${Date.now()}`,
      'popup=yes,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=1028,height=764',
    );
  };

  // The header label already says what each button does, so these tooltips
  // answer the thing the label can't: what "notes" covers.
  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Section Notes and Insights sections are not included in the PDF.</div>
      </Tooltip>
    );
  }

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Section Notes and Insights sections are included in the PDF.</div>
      </Tooltip>
    );
  }

  // Names the destination for what it is — labsmarts.com/exporttopdf is a
  // troubleshooting page, not a feature overview — and flags that it leaves
  // the app, which the two Export to PDF buttons do not.
  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Opens the Export to PDF help page: browser requirements and
          troubleshooting.
        </div>
      </Tooltip>
    );
  }

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis');
  };

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  toggleSection = (section: Section | string) => {
    this.setState(prev => {
      const already = prev.collapsedSections.includes(section as Section);
      return {
        collapsedSections: already
          ? prev.collapsedSections.filter(s => s !== section)
          : [...prev.collapsedSections, section as Section],
      };
    });
  };

  // Expand (if collapsed) and smooth-scroll to a section. Shared by the Results
  // Overview buttons and the Supplement Protocols section links.
  jumpToSection = (section: Section) => {
    const scrollToSection = () => {
      const element = document.getElementById(`section-${section}`);
      if (!element) return;
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    if (this.state.collapsedSections.includes(section)) {
      this.toggleSection(section);
      // Wait for the expand to flush to the DOM so the scroll target's final Y
      // position is what we measure.
      requestAnimationFrame(() => requestAnimationFrame(scrollToSection));
    } else {
      scrollToSection();
    }
  };

  // Like jumpToSection, but for the cross-reference links inside Possible Root
  // Causes text. Expands (if collapsed) and scrolls to the target section, and
  // when openCausesTab is set, activates that section's "Possible Root Causes"
  // tab. The section Tabs are uncontrolled react-bootstrap Tabs, so we open the
  // tab by clicking its nav link — scoped to the destination's #section-<X>
  // container so a sibling section's identically-named tab isn't touched.
  navigateToSection = (section: Section, openCausesTab: boolean) => {
    const run = () => {
      const element = document.getElementById(`section-${section}`);
      if (!element) return;
      if (openCausesTab) {
        const tab = Array.from(
          element.querySelectorAll('[role="tab"], a'),
        ).find(
          el => (el.textContent || '').trim() === 'Possible Root Causes',
        ) as HTMLElement | undefined;
        if (tab) tab.click();
      }
      // The section's top edge doesn't move when its inner tab content changes,
      // so measuring after the tab click is safe.
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    if (this.state.collapsedSections.includes(section)) {
      this.toggleSection(section);
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      requestAnimationFrame(run);
    }
  };

  // The "jump down" circle, bottom-right. Section Notes renders nothing when no
  // section has a note, so the button falls back to the Insights card that sits
  // directly below it.
  //
  // The fallback was landing short of the panel, because a scroll can never
  // take an element past the bottom of the document: window.scrollTo clamps to
  // (scrollHeight - innerHeight), and with Section Notes absent the report
  // often ends less than a screen below the Insights card (a collapsed
  // Insights, no supplement content, then just the disclaimer). Section Notes
  // being there is what used to supply that room — which is why the button
  // looked right until the panel was missing, the one case the fallback exists
  // for. So grow a spacer under the report by whatever the scroll is short,
  // then scroll. The spacer stays: taking it back would drag the report away
  // from the panel the practitioner just jumped to.
  jumpToSectionNotes = () => {
    const element =
      document.getElementById('section-notes') ||
      document.getElementById('insights');
    if (!element) return;
    const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
    const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
    const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
    const maxScroll =
      document.documentElement.scrollHeight - window.innerHeight;
    const shortfall = Math.ceil(top - maxScroll);
    if (shortfall > 0) {
      // `top` is measured above the spacer, so growing it doesn't move the
      // target — only the room underneath it. Scroll on the next frame, once
      // the taller page has actually been laid out.
      this.setState(
        { jumpSpacerHeight: this.state.jumpSpacerHeight + shortfall },
        () =>
          requestAnimationFrame(() =>
            window.scrollTo({ top, behavior: 'smooth' }),
          ),
      );
      return;
    }
    window.scrollTo({ top, behavior: 'smooth' });
  };

  toggleOutsideOptimalCharts = (panel: OutsideOptimalPanelKey) => {
    this.setState(prev => {
      const collapsed = prev.outsideOptimalChartsCollapsed.includes(panel);
      return {
        outsideOptimalChartsCollapsed: collapsed
          ? prev.outsideOptimalChartsCollapsed.filter(p => p !== panel)
          : [...prev.outsideOptimalChartsCollapsed, panel],
      };
    });
  };

  // Section-header "Show bar graphs" checkbox: expand-all when currently any
  // panel is collapsed, collapse-all when every panel is already expanded.
  toggleAllOutsideOptimalCharts = () => {
    this.setState(prev => ({
      outsideOptimalChartsCollapsed:
        prev.outsideOptimalChartsCollapsed.length === 0
          ? [...ALL_OUTSIDE_OPTIMAL_PANEL_KEYS]
          : [],
    }));
  };

  toggleTestResultsAnalysis() {
    this.setState({
      hideTestResultsAnalysis: !this.state.hideTestResultsAnalysis,
    });
  }

  private renderSectionSummary() {
    const { bloodworkAnalysis, hideTestResultsAnalysis } = this.state;
    if (!bloodworkAnalysis || hideTestResultsAnalysis) return null;

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Hide the entire Results Overview card when there are no markers to
    // summarize (e.g. a test with no blood values entered yet). Without this
    // the "Results Overview" header would render above an empty panel body.
    if (!hasResultsOverviewContent(
      bloodworkAnalysis,
      this.previousBloodworkAnalysis,
      hasWbcValue,
      this.props.currentUserProfile.showOnlySectionSignals,
      this.state.showOnlyImmuneSectionSignals,
    )) {
      return null;
    }

    const colorLegendTooltip = (
      // Wide on purpose: this legend has grown long enough that a narrow column
      // ran taller than the viewport and clipped off the top (Popper can shift a
      // popover, but it can't shrink one that doesn't fit). Trading width for
      // height keeps it on screen; the vw cap stops it overflowing narrow
      // windows, and the maxHeight scrolls rather than clips if it ever
      // outgrows the viewport again.
      <Popover
        id="results-overview-popover"
        style={{ maxWidth: 'min(760px, 92vw)', width: 'max-content' }}
      >
        <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div style={{ textAlign: 'left' }}>
          <strong>Each section's status reflects its most concerning marker:</strong>
          <br />
          <span style={{ color: '#5CB166' }}>&#9632;</span> <strong>Optimal</strong> — all markers within their optimal range
          <br />
          <span style={{ color: '#D8BE47' }}>&#9632;</span> <strong>Monitor</strong> — at least one marker outside optimal but within lab range
          <br />
          <span style={{ color: '#E1974B' }}>&#9632;</span> <strong>Support</strong> — at least one marker outside lab range but not in alarm range
          <br />
          <span style={{ color: '#E56A5A' }}>&#9632;</span> <strong>Priority</strong> — at least one marker in the alarm range
          <br /><br />
          {/* Paired with showTopAreas in results-overview-panel.tsx — the band
              is hidden, so this paragraph stays out of the legend. Restore both
              together, or the legend documents an element that isn't rendered.
          <strong>Top Areas to Review</strong> at the top of the panel gathers the most concerning sections from across all five groups, ranked by the same rules described below. Only <strong>Priority</strong> and <strong>Support</strong> sections qualify — it lists three, or five when more than three sections are Priority.
          <br /><br />
          */}
          The <strong>four circles at the top of each system section</strong> count how many of that system's <strong>sections</strong> fall into each status (Optimal / Monitor / Support / Priority).
          <br /><br />
          The <strong>thin bar under each section</strong> shows how that section's individual <strong>markers</strong> split across the same four statuses, each segment sized by how many markers fall in it, with the counts below and the section total beside it.
          <br /><br />
          Within each group, sections are sorted by status — most concerning first — and sections sharing a status are ordered by <strong>how many markers sit outside optimal</strong>, so a section with nine markers slightly off sits above one with three in the alarm range.
          <br /><br />
          A <strong>&#128077; thumbs up</strong> next to a section means it improved compared to the previous results. A <strong>&#128078; thumbs down</strong> means it worsened.
          <br /><br />
          Click any section to jump to it; the red arrow button &#8679; in the bottom-right corner brings you back here at any time.
        </div>
        </Popover.Content>
      </Popover>
    );

    const resultsOverviewCollapsed = this.state.collapsedSections.includes(Section.RESULTS_OVERVIEW);

    return (
      <Card className="mb-3" id="results-overview">
        <Card.Header
          onClick={() => this.toggleSection(Section.RESULTS_OVERVIEW)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>
            <FontAwesomeIcon icon={resultsOverviewCollapsed ? 'chevron-down' : 'chevron-up'} />
            {/* One uniform string, no size/weight/opacity overrides on the
                brand half — the whole title inherits the card header's white
                14px, matching the "Systemic Formulas (Supplements formulated by
                Dr. Shayne Morris)" header below. */}
            &nbsp;&nbsp;Results Overview — {FUNCTIONAL_MAP_LABEL}&nbsp;&nbsp;
            <span onClick={e => e.stopPropagation()}>
              {/* "right-start" (not "right") so the popover's TOP edge aligns
                  with the info icon and it grows downward. Plain "right" centers
                  it on the icon, which sent half of this tall legend above the
                  viewport where it was clipped with no way to scroll to it. */}
              <OverlayTrigger overlay={colorLegendTooltip} placement="right-start" trigger="click" rootClose>
                <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
              </OverlayTrigger>
            </span>
            <SectionVideoIcon section={Section.RESULTS_OVERVIEW} />
          </span>
          <label
            htmlFor="include-in-pdf-RESULTS_OVERVIEW"
            onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
            title="Include this section in the Analysis PDF"
          >
            <span style={{ marginRight: '6px' }}>Include in PDF</span>
            <input
              id="include-in-pdf-RESULTS_OVERVIEW"
              type="checkbox"
              className="lsm-styled-checkbox"
              checked={this.isIncludedInPdf(Section.RESULTS_OVERVIEW)}
              onChange={() => this.toggleIncludeInPdf(Section.RESULTS_OVERVIEW)}
            />
          </label>
        </Card.Header>
        {!resultsOverviewCollapsed && (
          <Card.Body>
            <ResultsOverviewPanel
              bloodworkAnalysis={bloodworkAnalysis}
              previousBloodworkAnalysis={this.previousBloodworkAnalysis}
              hasWbcValue={hasWbcValue}
              direction="row"
              showOnlySectionSignals={this.props.currentUserProfile.showOnlySectionSignals}
              showOnlyImmuneSectionSignals={this.state.showOnlyImmuneSectionSignals}
              showImmuneAbsolutes={this.state.showImmuneAbsolutes}
              onSectionClick={this.jumpToSection}
            />
          </Card.Body>
        )}
      </Card>
    );
  }

  render() {
    console.log('BloodworkTestAnalysis.render......');
    const {
      bloodworkAnalysis,
      showUpgradeModal,
      hideTestResultsAnalysis,
      previousBloodwork,
      collapsedSections,
      excludedFromClientList,
      clientListSaveState,
      supplementEdits,
      jumpSpacerHeight,
    } = this.state;
    const { furtherConsiderations, bloodworkEntryStore } = this.props;
    // The checkbox column on the by-system cards. Built here rather than in the
    // panel so the card below reads the same selection the column writes.
    const clientList = clientListSelection(
      excludedFromClientList,
      this.toggleClientListProduct,
    );

    if (!bloodworkAnalysis) {
      return null;
    }

    const isCollapsed = (section: Section | string) =>
      collapsedSections.includes(section as Section);

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;
    const previousHasWbcValue =
      (this.previousBloodworkAnalysis as any)?.wbc?.hasValue?.() === true;

    // When comparing to a previous test, optionally append "previous-only"
    // markers to a section's reference-range charts: markers that had a value
    // last time but not now, plotted with just their previous (blue) blood
    // drop. Gated on the practitioner's setting and on the presence of a
    // previous test. Sections with NO current markers are augmented too — they
    // then render as previous-only bar graphs rather than disappearing (see
    // makeWithPreviousOnly). Their Results Overview pill still stays hidden:
    // there is no current status to report.
    const showPreviousOnly =
      (this.props.currentUserProfile.showPreviousTestMarkersInComparison ?? true) &&
      !!this.previousBloodworkAnalysis;
    const withPreviousOnly = makeWithPreviousOnly(
      bloodworkAnalysis,
      this.previousBloodworkAnalysis,
      this.props.currentUserProfile.showPreviousTestMarkersInComparison,
    );

    // Build Above/Below Optimal marker lists (same logic as the PDF view)
    //
    // Practitioner-typed markers filed into a section ride along in that
    // section's getter (see BloodworkAnalysis.withCustomMarkers) and are counted
    // here like any other marker — the same rule the Results Overview follows.
    // A marker that colors its section's box has to be listed among the markers
    // outside optimal that explain the color. Their MOO heading resolves for
    // free: buildSystemGroupDefs reads these same getters.
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getStressAdrenalPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    // Direction-split groups for the "Markers Outside Optimal" 6-panel layout.
    // Derived from the 3 zone groups above so all EGFR/RDW/higher-is-better
    // edge cases are inherited unchanged. EGFR markers (higher-is-better) only
    // ever flag when low, so they naturally land in the low-side panels.
    // RDW markers only flag high, so they only land in the high-side panels.
    const isHighSide = (pm: PanelMarker) =>
      !isHigherIsBetter(pm) &&
      (pm.isGreaterThanAlarm?.() || pm.isGreaterThanLab?.() || pm.isGreaterThanOptimal?.());

    const highAlarmGroup    = needsAttentionGroup.filter(isHighSide);
    const lowAlarmGroup     = needsAttentionGroup.filter(pm => !isHighSide(pm));
    const aboveLabGroup     = worthDiscussingGroup.filter(isHighSide);
    const belowLabGroup     = worthDiscussingGroup.filter(pm => !isHighSide(pm));
    const aboveOptimalGroup = fineTuneGroup.filter(isHighSide);
    const belowOptimalGroup = fineTuneGroup.filter(pm => !isHighSide(pm));

    // System grouping for "Markers Outside Optimal" lives in the shared
    // mooSystemGrouping module so the web view and the PDF export stay in sync.
    // Markers group by their primary home; cross-cutting relevance (Inflammation /
    // Glucose) is shown per-marker via the secondary tagger.
    const mooSystemDefs = buildSystemGroupDefs(bloodworkAnalysis);
    const attrToSystem = buildAttrToSystem(mooSystemDefs);
    const secondaryTags = buildSecondaryTagger(mooSystemDefs);
    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => appendSecondaryLabels(groupMarkersBySystem(markers, scoreOf, attrToSystem), secondaryTags);

    const collectionDate = bloodworkAnalysis.bloodwork.collectionDate;
    const client = bloodworkAnalysis.bloodwork.client;
    const panelTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    const evalLarge = 3;
    const evalSmall = 12;

    const refLarge = 9;
    const refSmall = 12;

    const hideTestResultsAnalysisIconName = hideTestResultsAnalysis
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <>
        <Card id="test-results-analysis-panel">
          <Card.Header>
            <FontAwesomeIcon
              icon={hideTestResultsAnalysisIconName}
              onClick={() => this.toggleTestResultsAnalysis()}
            />
            &nbsp;&nbsp;Test Results Analysis&nbsp;
            <OverlayTrigger overlay={panelTooltip}>
              <FontAwesomeIcon
                icon="external-link-alt"
                onClick={this.openWindow}
              />
            </OverlayTrigger>
            {/* Flex row, not inline text: the label has to sit on the icon's
                vertical centre (same as the "Include BMI in PDF" label), which
                baseline alignment plus Font Awesome's -0.125em vertical-align
                does not give. 12px gap reproduces the three &nbsp; that used to
                separate the icons. */}
            <span
              className="float-right"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}
            >
              <OverlayTrigger
                overlay={this.exportInfoTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon="info-circle"
                  onClick={this.openExportInfoWindow}
                />
              </OverlayTrigger>
              {/* Each export icon carries its label to the left, matching how
                  every other icon-driven action in the app is labelled. The
                  label and the icon are one click target. */}
              <OverlayTrigger overlay={this.exportTooltip()} placement="bottom">
                <span
                  onClick={() => this.export(false)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Export to PDF without notes
                  <FontAwesomeIcon icon={faFileDownload} />
                </span>
              </OverlayTrigger>
              <OverlayTrigger
                overlay={this.exportWithNotesTooltip()}
                placement="bottom"
              >
                <span
                  onClick={() => this.export(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Export to PDF with notes
                  <FontAwesomeIcon icon={faFileDownloadSolid} />
                </span>
              </OverlayTrigger>
            </span>
          </Card.Header>
          {/* paddingTop trimmed to 10px and the Client Information card's own
              margin-top zeroed so the gap above it matches the 10px that the
              collapsed sibling margins leave between it and Results Overview. */}
          {hideTestResultsAnalysis ? null : (
            <Card.Body style={{ paddingTop: 10 }}>
              <Card style={{ marginTop: 0 }}>
                <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Client Information</span>
                  <label
                    htmlFor="include-in-pdf-BMI"
                    style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                    title="Include the BMI chart in the Analysis PDF"
                  >
                    <span style={{ marginRight: '6px' }}>Include BMI in PDF</span>
                    <input
                      id="include-in-pdf-BMI"
                      type="checkbox"
                      className="lsm-styled-checkbox"
                      checked={this.isIncludedInPdf(Section.BMI)}
                      onChange={() => this.toggleIncludeInPdf(Section.BMI)}
                    />
                  </label>
                </Card.Header>
                <Card.Body>
                  <div>
                    {this.state.editingClientInfo && this.state.clientInfoValidationErrors.length > 0 && (
                      <div className="alert alert-danger" style={{ padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                        <strong>Please fix the following before saving:</strong>
                        <ul style={{ marginBottom: 0, paddingLeft: 20, marginTop: 4 }}>
                          {this.state.clientInfoValidationErrors.map((m, i) => (
                            <li key={i}>{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(() => {
                      const editing = this.state.editingClientInfo;
                      const eff: Client = (editing && this.state.pendingClient) ? this.state.pendingClient : client;
                      const isFemale = eff.gender === Gender.FEMALE;
                      const isPregnant = isFemale && eff.menopause === Menopause.PREGNANT;
                      const units: 'US' | 'SI' = eff.units === 'SI' ? 'SI' : 'US';

                      const bw = bloodworkAnalysis.bloodwork as any;
                      const heightCm = eff.height != null ? Number(eff.height) : (bw.height != null ? Number(bw.height) : null);
                      const weightLbs = eff.weight != null ? Number(eff.weight) : (bw.weight != null ? Number(bw.weight) : null);
                      const bmiNumber = heightCm != null && weightLbs != null
                        ? MathUtils.bmi(weightLbs, heightCm)
                        : null;
                      const bmiSex: 'M' | 'F' | undefined = eff.gender === Gender.MALE
                        ? 'M'
                        : eff.gender === Gender.FEMALE
                          ? 'F'
                          : undefined;

                      const patch = this.patchPendingClient;
                      const editableCellStyle: React.CSSProperties = editing
                        ? { backgroundColor: '#fff', verticalAlign: 'middle' }
                        : { verticalAlign: 'middle' };
                      const inputStyle: React.CSSProperties = {
                        width: '100%',
                        minWidth: 70,
                        fontSize: 'inherit',
                        padding: '3px 5px',
                        border: '1px solid #ced4da',
                        borderRadius: 3,
                        outline: 'none',
                        backgroundColor: '#fff',
                      };
                      const smallInputStyle: React.CSSProperties = { ...inputStyle, minWidth: 0, width: 48 };

                      const textTd = (val: string, onChange: (v: string) => void) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <input type="text" maxLength={50} value={val ?? ''}
                                onChange={(e) => onChange(e.target.value)} style={inputStyle} />
                            : (val || <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const selectTd = (
                        val: string,
                        opts: { value: string; label: string }[],
                        display: React.ReactNode,
                        onChange: (v: string) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <select value={val ?? ''} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
                                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const dateTd = (
                        isoVal: string | undefined,
                        display: React.ReactNode,
                        onChange: (iso: string | undefined) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <DatePicker
                                selected={isoVal ? moment(isoVal).toDate() : null}
                                onChange={(d: Date | null) =>
                                  onChange(d ? moment(d).startOf('day').toISOString() : undefined)}
                                maxDate={new Date()}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                dateFormat="MM/dd/yyyy"
                                customInput={<input style={inputStyle} />}
                              />
                            : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const numberTd = (
                        val: number | undefined,
                        display: React.ReactNode,
                        onChange: (v: number | undefined) => void,
                        min?: number,
                        max?: number,
                        suffix?: string,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number" min={min} max={max}
                                value={val ?? ''}
                                onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                                style={smallInputStyle}
                              />
                              {suffix && <span style={{ fontSize: 11, color: '#666' }}>{suffix}</span>}
                            </span>
                          ) : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)}
                        </td>
                      );

                      const booleanTd = (
                        val: boolean,
                        display: React.ReactNode,
                        onChange: (v: boolean) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <select value={val ? 'yes' : 'no'} onChange={(e) => onChange(e.target.value === 'yes')} style={inputStyle}>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            : display
                          }
                        </td>
                      );

                      const heightTd = () => {
                        if (!editing) {
                          return <td style={editableCellStyle}>{eff.height != null ? client.displayHeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                        }
                        if (units === 'US') {
                          const { ft, inches } = eff.height != null
                            ? MathUtils.cmToFtIn(eff.height)
                            : { ft: '' as any, inches: '' as any };
                          const ftStr = ft === '' ? '' : String(ft);
                          const inStr = inches === '' ? '' : String(inches);
                          return (
                            <td style={editableCellStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <div style={{ width: 70 }}>
                                  <Select<HeightOption>
                                    isSearchable={false}
                                    maxMenuHeight={400}
                                    options={FT_OPTIONS}
                                    styles={HEIGHT_SELECT_STYLES}
                                    value={FT_OPTIONS.find(o => o.value === ftStr) ?? FT_OPTIONS[0]}
                                    onChange={(opt) => {
                                      const v = opt?.value ?? '';
                                      const newFt = v === '' ? 0 : Number(v);
                                      const curIn = eff.height != null ? MathUtils.cmToFtIn(eff.height).inches : 0;
                                      patch((c) => { c.height = v === '' && (inches === 0 || inches === '')
                                        ? undefined
                                        : MathUtils.ftInToCm(newFt, Number(curIn) || 0); });
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: 11, color: '#666' }}>ft</span>
                                <div style={{ width: 70 }}>
                                  <Select<HeightOption>
                                    isSearchable={false}
                                    maxMenuHeight={400}
                                    options={IN_OPTIONS}
                                    styles={HEIGHT_SELECT_STYLES}
                                    value={IN_OPTIONS.find(o => o.value === inStr) ?? IN_OPTIONS[0]}
                                    onChange={(opt) => {
                                      const v = opt?.value ?? '';
                                      const newIn = v === '' ? 0 : Number(v);
                                      const curFt = eff.height != null ? MathUtils.cmToFtIn(eff.height).ft : 0;
                                      patch((c) => { c.height = v === '' && (ft === 0 || ft === '')
                                        ? undefined
                                        : MathUtils.ftInToCm(Number(curFt) || 0, newIn); });
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: 11, color: '#666' }}>in</span>
                              </span>
                            </td>
                          );
                        }
                        return (
                          <td style={editableCellStyle}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <input type="number" min={0}
                                value={eff.height ?? ''}
                                onChange={(e) => patch((c) => { c.height = e.target.value === '' ? undefined : Number(e.target.value); })}
                                style={{ ...smallInputStyle, width: 58 }} />
                              <span style={{ fontSize: 11, color: '#666' }}>cm</span>
                            </span>
                          </td>
                        );
                      };

                      const weightTd = () => {
                        if (!editing) {
                          return <td style={editableCellStyle}>{eff.weight != null ? client.displayWeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                        }
                        const displayed = eff.weight == null
                          ? ''
                          : units === 'SI' ? MathUtils.lbsToKg(eff.weight) : eff.weight;
                        return (
                          <td style={editableCellStyle}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <input type="number" min={0} max={units === 'SI' ? 453 : 999}
                                value={displayed === '' ? '' : displayed}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') { patch((c) => { c.weight = undefined; }); return; }
                                  const n = Number(raw);
                                  if (isNaN(n)) return;
                                  patch((c) => { c.weight = units === 'SI' ? MathUtils.kgToLbs(n) : n; });
                                }}
                                style={{ ...smallInputStyle, width: 58 }} />
                              <span style={{ fontSize: 11, color: '#666' }}>{units === 'SI' ? 'kg' : 'lbs'}</span>
                            </span>
                          </td>
                        );
                      };

                      const ethnicityOptions = [
                        { value: '',                                label: '—' },
                        { value: Ethnicity.WHITE,                   label: Ethnicity.WHITE },
                        { value: Ethnicity.AFRICAN_AMERICAN,        label: Ethnicity.AFRICAN_AMERICAN },
                        { value: Ethnicity.HISPANIC,                label: Ethnicity.HISPANIC },
                        { value: Ethnicity.ASIAN,                   label: Ethnicity.ASIAN },
                        { value: Ethnicity.OTHER,                   label: Ethnicity.OTHER },
                      ];
                      const menopauseOptions = [
                        { value: Menopause.PREMENOPAUSE,  label: 'Premenopause' },
                        { value: Menopause.POSTMENOPAUSE, label: 'Postmenopause' },
                        { value: Menopause.PREGNANT,      label: 'Pregnant' },
                      ];
                      const genderOptions = [
                        { value: Gender.MALE,   label: 'Male' },
                        { value: Gender.FEMALE, label: 'Female' },
                      ];
                      const trimesterOptions = [
                        { value: '',  label: '—' },
                        { value: '1', label: 'First' },
                        { value: '2', label: 'Second' },
                        { value: '3', label: 'Third' },
                      ];

                      return (
                        <>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                          <Table striped bordered hover className="labsmartz-table" style={{ marginBottom: 0, width: 'auto' }}>
                            <thead>
                              <tr>
                                <th>Last Name</th>
                                <th>First Name</th>
                                <th>Sex</th>
                                <th>Collection Date</th>
                                <th>Age at Collection</th>
                                <th>Date of Birth</th>
                                <th>Ethnicity</th>
                                {isFemale && <th>Menopause/Pregnant</th>}
                                {isPregnant && <th>Trimester</th>}
                                <th>Elevation</th>
                                <th>Inhaled Smoke</th>
                                <th>Alcohol Drinker</th>
                                {previousBloodwork && <th>Previous Collection Date</th>}
                                <th>Height</th>
                                <th>Weight</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="client-row">
                                {textTd(eff.lastName, (v) => patch((c) => { c.lastName = v; }))}
                                {textTd(eff.firstName, (v) => patch((c) => { c.firstName = v; }))}
                                {selectTd(eff.gender ?? '', genderOptions, eff.displayGender,
                                  (v) => patch((c) => { c.gender = v as Gender; }))}
                                <td title="Collection Date is set when the test is created; edit it from the test entry form."
                                  style={{ verticalAlign: 'middle' }}>
                                  <Moment format="MM/DD/YYYY">{collectionDate}</Moment>
                                </td>
                                <td title="Derived from Date of Birth and Collection Date."
                                  style={{ verticalAlign: 'middle' }}>{eff.ageAsOf(collectionDate)}</td>
                                {dateTd(
                                  eff.dateOfBirth,
                                  <Moment format="MM/DD/YYYY">{eff.dateOfBirth}</Moment>,
                                  (iso) => patch((c) => { c.dateOfBirth = iso; }),
                                )}
                                {selectTd(eff.ethnicity ?? '', ethnicityOptions, eff.displayEthnicity,
                                  (v) => patch((c) => { c.ethnicity = v as Ethnicity; }))}
                                {isFemale && selectTd(
                                  eff.menopause ?? '', menopauseOptions, eff.displayMenopause,
                                  (v) => patch((c) => {
                                    c.menopause = v as Menopause;
                                    if (v !== Menopause.PREGNANT) { c.trimester = undefined; }
                                  }),
                                )}
                                {isPregnant && selectTd(
                                  eff.trimester != null ? String(eff.trimester) : '',
                                  trimesterOptions,
                                  eff.displayTrimester,
                                  (v) => patch((c) => { c.trimester = v ? Number(v) : undefined; }),
                                )}
                                {numberTd(eff.elevation, eff.displayElevation,
                                  (v) => patch((c) => { c.elevation = v; }),
                                  0, 20000, 'feet')}
                                {booleanTd(!!eff.cigaretteSmoker, eff.displayInhaledSmoke,
                                  (v) => patch((c) => { c.cigaretteSmoker = v; }))}
                                {booleanTd(!!eff.alcoholDrinker, eff.displayAlcoholDrinker,
                                  (v) => patch((c) => { c.alcoholDrinker = v; }))}
                                {previousBloodwork && (
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <Moment format="MM/DD/YYYY">{previousBloodwork.collectionDate}</Moment>
                                  </td>
                                )}
                                {heightTd()}
                                {weightTd()}
                              </tr>
                            </tbody>
                          </Table>
                          {this.props.updateClient && (
                            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'center' }}>
                              {!editing ? (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={this.startEditClientInfo}
                                >
                                  Edit
                                </button>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={this.saveClientInfo}
                                    disabled={this.state.isSavingClientInfo}
                                  >
                                    {this.state.isSavingClientInfo ? 'Saving…' : 'Save'}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={this.cancelEditClientInfo}
                                    disabled={this.state.isSavingClientInfo}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const bmiAgeYears = eff.ageAsOf(collectionDate);
                          if (bmiNumber == null || isPregnant) return null;
                          if (bmiAgeYears != null && bmiAgeYears < 2) return null;
                          return (
                            <div style={{ marginTop: 12 }}>
                              <BMIBarChart
                                bmi={bmiNumber}
                                ageYears={bmiAgeYears}
                                sex={bmiSex}
                              />
                            </div>
                          );
                        })()}
                        </>
                      );
                    })()}
                    {this.props.currentUserProfile.preparedBy && (
                      <div style={{ marginTop: 12 }}>
                        <strong>Prepared by:</strong>{' '}
                        {this.props.currentUserProfile.preparedBy}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
              {this.renderSectionSummary()}

              <div id={`section-${Section.RBC}`}>
                <BloodworkTestAnalysisSection
                  section={Section.RBC}
                  panelTitle="Blood Oxygen & Red Blood Cell Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getRBCPanelMarkers(), a => a.getRBCPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#rbc"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.RBC)}
                  onToggle={() => this.toggleSection(Section.RBC)}
                  includeInPdf={this.isIncludedInPdf(Section.RBC)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.RBC)}
                />
              </div>

              <div id={`section-${Section.IRON}`}>
                <BloodworkTestAnalysisSection
                  section={Section.IRON}
                  panelTitle="Iron Status & Utilization"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getIronPanelMarkers(), a => a.getIronPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#iron"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.IRON)}
                  onToggle={() => this.toggleSection(Section.IRON)}
                  includeInPdf={this.isIncludedInPdf(Section.IRON)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.IRON)}
                />
              </div>

              <div id={`section-${Section.GLUCOSE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.GLUCOSE}
                  panelTitle="Glucose Regulation & Metabolic Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(
                    (() => {
                      const all = bloodworkAnalysis.getGlucosePanelMarkers();
                      return isHdlHighSoleOutOfOptimal(all)
                        ? all.filter(pm => pm.attribute !== 'hdl')
                        : all;
                    })(),
                    a => a.getGlucosePanelMarkers(),
                  )}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#glucose"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.GLUCOSE)}
                  onToggle={() => this.toggleSection(Section.GLUCOSE)}
                  includeInPdf={this.isIncludedInPdf(Section.GLUCOSE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.GLUCOSE)}
                />
              </div>

              <div id={`section-${Section.LIPIDS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIPIDS}
                  panelTitle="Lipid Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getLipidsPanelMarkers(), a => a.getLipidsPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#lipids"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIPIDS)}
                  onToggle={() => this.toggleSection(Section.LIPIDS)}
                  includeInPdf={this.isIncludedInPdf(Section.LIPIDS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.LIPIDS)}
                />
              </div>

              <div id={`section-${Section.THYROID}`}>
                <BloodworkTestAnalysisSection
                  section={Section.THYROID}
                  panelTitle="Thyroid Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getThyroidPanelMarkers(), a => a.getThyroidPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#thyroid"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.THYROID)}
                  onToggle={() => this.toggleSection(Section.THYROID)}
                  includeInPdf={this.isIncludedInPdf(Section.THYROID)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.THYROID)}
                />
              </div>

              {/*
                Digestion is UNFINISHED and was previously ungated, so it
                shipped to prod: possibleRootCauseCategoriesFor has no
                DIGESTION case, so its 15 authored pattern rows reach nothing,
                there is no insights panel, and its Supplements rows are
                unplumbed. What renders is a bare marker panel borrowed from
                other sections. Gated to dev + slava 2026-08-21 so the user can
                review it without practitioners seeing a half-built section.
                Pairs with the matching gate on the Results Overview entry.
              */}
              {digestionVisible() && (
              <div id={`section-${Section.DIGESTION}`}>
                <BloodworkTestAnalysisSection
                  section={Section.DIGESTION}
                  panelTitle="Digestive & Gut Health"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getDigestionPanelMarkers(), a => a.getDigestionPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#digestion"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.DIGESTION)}
                  onToggle={() => this.toggleSection(Section.DIGESTION)}
                  includeInPdf={this.isIncludedInPdf(Section.DIGESTION)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.DIGESTION)}
                />
              </div>
              )}

              <div id={`section-${Section.NUTRIENTS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.NUTRIENTS}
                  panelTitle="Vitamin & Mineral Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getMineralPanelMarkers(), a => a.getMineralPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#nutrients"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.NUTRIENTS)}
                  onToggle={() => this.toggleSection(Section.NUTRIENTS)}
                  includeInPdf={this.isIncludedInPdf(Section.NUTRIENTS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.NUTRIENTS)}
                />
              </div>

              <div id={`section-${Section.PROTEINS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROTEINS}
                  panelTitle="Protein Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getProteinsPanelMarkers(), a => a.getProteinsPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#proteins"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROTEINS)}
                  onToggle={() => this.toggleSection(Section.PROTEINS)}
                  includeInPdf={this.isIncludedInPdf(Section.PROTEINS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PROTEINS)}
                />
              </div>

              {/* Immune Activity hangs off WBC itself — no WBC, no immune
                  analysis. When the PREVIOUS test had one and this one doesn't,
                  the section still renders so its previous-only bar graphs
                  show, and WBCEvaluation comes with them: the panel is built
                  from whichever collection those bars came from and captioned
                  with its date (insightsSourceAnalysis), the same rule every
                  other section's Insights follows. It is gated on the WBC value
                  of THAT analysis in BloodworkTestAnalysisSection, not on the
                  current test's. */}
              {(hasWbcValue || (showPreviousOnly && previousHasWbcValue)) && (
                <div id={`section-${Section.WBC}`}>
                  <BloodworkTestAnalysisSection
                    section={Section.WBC}
                    panelTitle="Immune Activity"
                    bloodworkAnalysis={bloodworkAnalysis}
                    panelMarkers={withPreviousOnly(
                      (() => {
                        const all = bloodworkAnalysis.getWBCPanelMarkers();
                        const base = this.state.showImmuneAbsolutes ? all : all.filter(pm => !isImmuneAbsolute(pm));
                        return this.state.showOnlyImmuneSectionSignals
                          ? suppressAlpWhenZincSupersedes(base.filter(pm => !isGlobulinOptimal(pm)).filter(keepInImmunePanel), all)
                          : base;
                      })(),
                      a => a.getWBCPanelMarkers(),
                    )}
                    furtherConsiderations={furtherConsiderations}
                    evalSmall={evalSmall}
                    refSmall={refSmall}
                    evalLarge={evalLarge}
                    refLarge={refLarge}
                    infoLink="https://labsmarts.com/bibliography#wbc"
                    bloodworkEntryStore={bloodworkEntryStore}
                    enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                    collapsed={isCollapsed(Section.WBC)}
                    onToggle={() => this.toggleSection(Section.WBC)}
                    includeInPdf={this.isIncludedInPdf(Section.WBC)}
                    onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.WBC)}
                    extraToggles={[
                      {
                        id: 'immune-only-section-signals',
                        label: 'Section Signals Only',
                        checked: this.state.showOnlyImmuneSectionSignals,
                        onToggle: this.toggleShowOnlyImmuneSectionSignals,
                        title: 'Only show Immune markers that contribute a signal to this section',
                      },
                      {
                        id: 'immune-show-absolutes',
                        label: 'Show Absolutes',
                        checked: this.state.showImmuneAbsolutes,
                        onToggle: this.toggleShowImmuneAbsolutes,
                        title: 'Show the absolute differential bar graphs (Neutrophils, Lymphocytes, Monocytes, Eosinophils, Basophils)',
                      },
                    ]}
                  />
                </div>
              )}

              <div id={`section-${Section.INFLAMMATION_OXIDATIVE_STRESS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                  panelTitle="Inflammation & Oxidative Stress"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(
                    (() => {
                      const all = bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers();
                      return this.props.currentUserProfile.showOnlySectionSignals
                        ? all.filter(keepInInflammationPanel)
                        : all;
                    })(),
                    a => a.getInflammationOxidativeStressPanelMarkers(),
                  )}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#inflammation"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  onToggle={() => this.toggleSection(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  includeInPdf={this.isIncludedInPdf(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                />
              </div>

              <div id={`section-${Section.PLATELETS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PLATELETS}
                  panelTitle="Blood Clotting & Flow"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getPlateletPanelMarkers(), a => a.getPlateletPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#platelets"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PLATELETS)}
                  onToggle={() => this.toggleSection(Section.PLATELETS)}
                  includeInPdf={this.isIncludedInPdf(Section.PLATELETS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PLATELETS)}
                />
              </div>

              <div id={`section-${Section.LIVER_BILIARY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIVER_BILIARY}
                  panelTitle="Liver Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getLiverPanelMarkers(), a => a.getLiverPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#liver"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIVER_BILIARY)}
                  onToggle={() => this.toggleSection(Section.LIVER_BILIARY)}
                  includeInPdf={this.isIncludedInPdf(Section.LIVER_BILIARY)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.LIVER_BILIARY)}
                />
              </div>

              <div id={`section-${Section.KIDNEY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.KIDNEY}
                  panelTitle="Kidney Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getKidneyPanelMarkers(), a => a.getKidneyPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#kidney"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.KIDNEY)}
                  onToggle={() => this.toggleSection(Section.KIDNEY)}
                  includeInPdf={this.isIncludedInPdf(Section.KIDNEY)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.KIDNEY)}
                />
              </div>

              <div id={`section-${Section.ELECTROLYTES}`}>
                <BloodworkTestAnalysisSection
                  section={Section.ELECTROLYTES}
                  panelTitle="Electrolyte Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getElectrolytePanelMarkers(), a => a.getElectrolytePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#electrolytes"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.ELECTROLYTES)}
                  onToggle={() => this.toggleSection(Section.ELECTROLYTES)}
                  includeInPdf={this.isIncludedInPdf(Section.ELECTROLYTES)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.ELECTROLYTES)}
                />
              </div>

              <div id={`section-${Section.STRESS_ADRENAL}`}>
                <BloodworkTestAnalysisSection
                  section={Section.STRESS_ADRENAL}
                  panelTitle="Stress & Adrenal Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getStressAdrenalPanelMarkers(), a => a.getStressAdrenalPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#stress-adrenal"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.STRESS_ADRENAL)}
                  onToggle={() => this.toggleSection(Section.STRESS_ADRENAL)}
                  includeInPdf={this.isIncludedInPdf(Section.STRESS_ADRENAL)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.STRESS_ADRENAL)}
                />
              </div>

              <div id={`section-${Section.SEX_HORMONE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.SEX_HORMONE}
                  panelTitle="Hormone Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getSexHormonePanelMarkers(), a => a.getSexHormonePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#hormones"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.SEX_HORMONE)}
                  onToggle={() => this.toggleSection(Section.SEX_HORMONE)}
                  includeInPdf={this.isIncludedInPdf(Section.SEX_HORMONE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.SEX_HORMONE)}
                />
              </div>

              <div id={`section-${Section.PROSTATE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROSTATE}
                  panelTitle="Prostate Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getProstatePanelMarkers(), a => a.getProstatePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#prostate"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROSTATE)}
                  onToggle={() => this.toggleSection(Section.PROSTATE)}
                  includeInPdf={this.isIncludedInPdf(Section.PROSTATE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PROSTATE)}
                />
              </div>

              {/* Markers the practitioner typed straight onto this test because
                  LabSmarts does not ship them yet (Bloodwork.customMarkers).
                  Bar graphs only — no insights, root causes, supplements,
                  Results Overview pill or Markers Outside Optimal entry. The
                  section hides itself when there are none (the panel returns
                  null for an empty marker list).

                  withPreviousOnly works here now: populateCustomPanelMarkers()
                  builds a value-less stand-in for a custom marker the previous
                  test carried and this one doesn't, which is the current
                  PanelMarker getPreviousOnlyPanelMarkers needs to hang the
                  previous value on. Before that, such a marker was a row in the
                  comparison TABLE with no bar here. */}
              {/* Sections the practitioner named themselves, one panel each,
                  before the catch-all appendix. Rendered as CUSTOM_MARKERS so
                  they get the same plain treatment (bar graphs only, no
                  insights, no root causes, no Results Overview pill) — only the
                  title and the collapse/PDF key differ. */}
              {bloodworkAnalysis.getPractitionerSectionNames().map(name => {
                const key = practitionerSectionStateKey(name);
                return (
                  <div id={`section-${key}`} key={key}>
                    <BloodworkTestAnalysisSection
                      section={Section.CUSTOM_MARKERS}
                      sectionKey={key}
                      practitionerSectionName={name}
                      panelTitle={practitionerSectionTitle(name)}
                      bloodworkAnalysis={bloodworkAnalysis}
                      panelMarkers={withPreviousOnly(
                        bloodworkAnalysis.getPractitionerSectionPanelMarkers(name),
                        a => a.getPractitionerSectionPanelMarkers(name),
                      )}
                      furtherConsiderations={furtherConsiderations}
                      evalSmall={evalSmall}
                      refSmall={refSmall}
                      evalLarge={evalLarge}
                      refLarge={refLarge}
                      bloodworkEntryStore={bloodworkEntryStore}
                      enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                      collapsed={isCollapsed(key)}
                      onToggle={() => this.toggleSection(key)}
                      includeInPdf={this.isIncludedInPdf(key)}
                      onIncludeInPdfToggle={() => this.toggleIncludeInPdf(key)}
                    />
                  </div>
                );
              })}

              <div id={`section-${Section.CUSTOM_MARKERS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.CUSTOM_MARKERS}
                  panelTitle={Section.CUSTOM_MARKERS}
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(
                    bloodworkAnalysis.getCustomMarkerPanelMarkers(),
                    a => a.getCustomMarkerPanelMarkers(),
                  )}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.CUSTOM_MARKERS)}
                  onToggle={() => this.toggleSection(Section.CUSTOM_MARKERS)}
                  includeInPdf={this.isIncludedInPdf(Section.CUSTOM_MARKERS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.CUSTOM_MARKERS)}
                />
              </div>

              {/* Immune & Inflammatory Burden Index (I³) — questionnaire-driven.
                  One row: the Weighted Score + Root Cause Framework panels are
                  FIXED widths; the Assessment/Testing/Intervention tabs flex to
                  fill the remaining width. Parked behind
                  SHOW_IMMUNE_INFLAMMATORY_SECTION while BWH Labs is on hold, and
                  env-gated to local dev only (hidden in slava + prod) under
                  that. */}
              {SHOW_IMMUNE_INFLAMMATORY_SECTION &&
                process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                ImmuneInflammatoryBurdenPanel.shouldRender(bloodworkAnalysis) && (
                  <div id={`section-${Section.IMMUNE_INFLAMMATORY}`}>
                    <Card>
                      <Card.Header
                        onClick={() => this.toggleSection(Section.IMMUNE_INFLAMMATORY)}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={isCollapsed(Section.IMMUNE_INFLAMMATORY) ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Immune &amp; Inflammatory Burden Index (I³)
                        <span style={{ fontWeight: 400, fontSize: '13px', opacity: 0.85 }}>
                          {' '}— Created by Robert Rountree, MD · Jocelyn Strand, ND · BWH Labs
                        </span>
                      </Card.Header>
                      {!isCollapsed(Section.IMMUNE_INFLAMMATORY) && (
                        <Card.Body>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '0 0 auto', width: '440px', maxWidth: '100%' }}>
                              <ImmuneInflammatoryBurdenPanel bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                            <div style={{ flex: '0 0 auto', width: '340px', maxWidth: '100%' }}>
                              <RootCauseFrameworkPanel bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                            <div style={{ flex: '1 1 360px', minWidth: '320px' }}>
                              <RootCauseGuidanceTabs bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                          </div>
                        </Card.Body>
                      )}
                    </Card>
                  </div>
                )}

              {/* Metabolic Vulnerability (MVX Plus). Reads the nine MVX values off
                  the current bloodwork (entered via the MVX Plus card in Add Test
                  Results); renders only when at least one is entered. Env-gated to
                  local dev only (hidden in slava + prod). */}
              {process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                MetabolicVulnerabilityInsights.shouldRender(bloodworkAnalysis) && (
                  <Card id="section-mvx">
                    <Card.Header
                      onClick={() => this.toggleSection(MVX_SECTION)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span>
                        <FontAwesomeIcon icon={isCollapsed(MVX_SECTION) ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Metabolic Vulnerability Index (MVX) Plus&nbsp;&nbsp;
                        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-block' }}>
                          <OverlayTrigger
                            placement="right"
                            trigger="click"
                            rootClose
                            overlay={
                              <Popover id="mvx-section-tooltip" style={{ maxWidth: 306 }}>
                                <Popover.Content>
                                  <div style={{ textAlign: 'left' }}>
                                    The <strong>Metabolic Vulnerability Index (MVX) Plus</strong> is a Labcorp NMR
                                    blood test (#123601) that scores near-term metabolic resilience from six markers,
                                    combined into two sub-scores — inflammation (IVX) and metabolic strain (MMX).
                                    <div style={{ marginTop: 6 }}>
                                      <a href={LABCORP_MVX_PLUS_URL} target="_blank" rel="noopener noreferrer">
                                        Learn more
                                      </a>
                                    </div>
                                  </div>
                                </Popover.Content>
                              </Popover>
                            }
                          >
                            <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
                          </OverlayTrigger>
                        </span>
                      </span>
                      <label
                        htmlFor="include-in-pdf-mvx"
                        onClick={e => e.stopPropagation()}
                        style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                        title="Include this section in the Analysis PDF"
                      >
                        <span style={{ marginRight: '6px' }}>Include in PDF</span>
                        <input
                          id="include-in-pdf-mvx"
                          type="checkbox"
                          className="lsm-styled-checkbox"
                          checked={this.isIncludedInPdf(MVX_SECTION)}
                          onChange={() => this.toggleIncludeInPdf(MVX_SECTION)}
                        />
                      </label>
                    </Card.Header>
                    {!isCollapsed(MVX_SECTION) && (
                      <>
                        <Card.Body>
                          <Row>
                            <Col xs={12} lg={3}>
                              <MetabolicVulnerabilityInsights bloodworkAnalysis={bloodworkAnalysis} />
                            </Col>
                            <Col xs={12} lg={9}>
                              <Tabs defaultActiveKey="mvx-reference" id="mvx-section-tabs" mountOnEnter>
                                <Tab eventKey="mvx-reference" title="Reference Range">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxResultsPanel bloodworkAnalysis={bloodworkAnalysis} />
                                  </div>
                                </Tab>
                                <Tab eventKey="mvx-references" title="References">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxReferencesSection />
                                  </div>
                                </Tab>
                                <Tab eventKey="mvx-about" title="About">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxAboutSection bloodworkAnalysis={bloodworkAnalysis} />
                                  </div>
                                </Tab>
                              </Tabs>
                            </Col>
                          </Row>
                          <MvxBlueprintPanel bloodworkAnalysis={bloodworkAnalysis} furtherConsiderations={furtherConsiderations} />
                        </Card.Body>
                        <Card.Footer>
                          <Footer />
                        </Card.Footer>
                      </>
                    )}
                  </Card>
                )}

              {/* Markers Outside Optimal. Deliberately near the bottom of the
                  report — it is a long, dense reference list rather than
                  something to read first, so it sits below the per-system
                  sections and directly above the consolidated Section Notes.
                  The PDF keeps its own placement (see
                  containers/bloodwork-analysis-pdf.tsx). */}
              {(() => {
                // Respect the user's display-mode preference: 'hideSection'
                // removes the panel from the page entirely.
                if (this.props.currentUserProfile?.markersOutsideOptimalDisplay === 'hideSection') {
                  return null;
                }

                const totalOutside =
                  highAlarmGroup.length + lowAlarmGroup.length +
                  aboveLabGroup.length + belowLabGroup.length +
                  aboveOptimalGroup.length + belowOptimalGroup.length;
                if (totalOutside === 0) return null;

                const isPanelExpanded = (key: OutsideOptimalPanelKey) =>
                  !this.state.outsideOptimalChartsCollapsed.includes(key);

                // Zone colors mirror the marker badges so each panel header signals
                // its severity at a glance (red = alarm, orange = lab, yellow = optimal).
                // Pastel panel-header palette — softened tints with dark text
                // (the alarm header moves off white text now that its background
                // is a pale coral). Kept in sync with the PDF zoneColors in
                // bloodwork-analysis-pdf.tsx and the .summary-marker-badge rules
                // in pdf.css.
                const zoneColors = (s: Section): { bg: string; fg: string } => {
                  if (s === Section.NEEDS_ATTENTION)  return { bg: '#F3B7AC', fg: '#7a2e20' };
                  if (s === Section.WORTH_DISCUSSING) return { bg: '#F7D6A9', fg: '#6b4400' };
                  return { bg: '#F1E7A9', fg: '#5a5200' };
                };

                type PanelDef = {
                  key: OutsideOptimalPanelKey;
                  title: string;
                  tooltipId: string;
                  tooltipHeading?: string;
                  tooltip: string;
                  tooltipSortedBy: string;
                  sectionForZone: Section;
                  markers: PanelMarker[];
                  scoreOf: (pm: PanelMarker) => number;
                };

                // Panel order = severity desc, high side first:
                //   High Alarm, Above Lab, Above Optimal, Low Alarm, Below Lab, Below Optimal
                // The first three (highs) stack in the left column and the last three
                // (lows) stack in the right column — see the two-column render below.
                const panelDefs: PanelDef[] = [
                  {
                    key: 'highAlarm',
                    title: 'High Alarm Range',
                    tooltipId: 'panel-tooltip-high-alarm',
                    tooltipHeading: 'High Priority',
                    tooltip: 'Markers whose values have crossed above the high alarm threshold.',
                    tooltipSortedBy: 'Sorted from most to least past the high alarm threshold.',
                    sectionForZone: Section.NEEDS_ATTENTION,
                    markers: highAlarmGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'alarm'),
                  },
                  {
                    key: 'aboveLab',
                    title: 'Above Lab Range',
                    tooltipId: 'panel-tooltip-above-lab',
                    tooltip: 'Markers above the lab range but not yet in the alarm range.',
                    tooltipSortedBy: 'Sorted from most to least past the upper lab threshold.',
                    sectionForZone: Section.WORTH_DISCUSSING,
                    markers: aboveLabGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'lab'),
                  },
                  {
                    key: 'aboveOptimal',
                    title: 'Above Optimal Range',
                    tooltipId: 'panel-tooltip-above-optimal',
                    tooltip: 'Markers above the optimal range but still within the lab range, so a standard lab report may not flag them as abnormal.',
                    tooltipSortedBy: 'Sorted from most to least past the upper optimal threshold.',
                    sectionForZone: Section.FINE_TUNE,
                    markers: aboveOptimalGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'optimal'),
                  },
                  {
                    key: 'lowAlarm',
                    title: 'Low Alarm Range',
                    tooltipId: 'panel-tooltip-low-alarm',
                    tooltipHeading: 'High Priority',
                    tooltip: 'Markers whose values have crossed below the low alarm threshold.',
                    tooltipSortedBy: 'Sorted from most to least past the low alarm threshold.',
                    sectionForZone: Section.NEEDS_ATTENTION,
                    markers: lowAlarmGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'alarm'),
                  },
                  {
                    key: 'belowLab',
                    title: 'Below Lab Range',
                    tooltipId: 'panel-tooltip-below-lab',
                    tooltip: 'Markers below the lab range but not yet in the alarm range.',
                    tooltipSortedBy: 'Sorted from most to least past the lower lab threshold.',
                    sectionForZone: Section.WORTH_DISCUSSING,
                    markers: belowLabGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'lab'),
                  },
                  {
                    key: 'belowOptimal',
                    title: 'Below Optimal Range',
                    tooltipId: 'panel-tooltip-below-optimal',
                    tooltip: 'Markers below the optimal range but still within the lab range, so a standard lab report may not flag them as abnormal.',
                    tooltipSortedBy: 'Sorted from most to least past the lower optimal threshold.',
                    sectionForZone: Section.FINE_TUNE,
                    markers: belowOptimalGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'optimal'),
                  },
                ];

                const renderPanel = (def: PanelDef) => {
                  if (def.markers.length === 0) return null;
                  const expanded = isPanelExpanded(def.key);
                  const colors = zoneColors(def.sectionForZone);
                  return (
                    <Card
                      key={def.key}
                      className="summary-section-card"
                      // Fixed 400px width so every panel is identical width
                      // (axis labels never get clipped). Height is intrinsic so the
                      // panel only grows as tall as its markers need.
                      style={{ width: 400, borderColor: colors.bg }}
                    >
                      <Card.Header
                        onClick={() => this.toggleOutsideOptimalCharts(def.key)}
                        style={{
                          cursor: 'pointer',
                          // .card-header in index.css paints a blue gradient via
                          // background-image, which would mask backgroundColor.
                          backgroundImage: 'none',
                          backgroundColor: colors.bg,
                          color: colors.fg,
                          borderBottomColor: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>
                          <FontAwesomeIcon icon={expanded ? 'chevron-up' : 'chevron-down'} />
                          &nbsp;&nbsp;{def.title}&nbsp;({def.markers.length})
                        </span>
                        <span onClick={e => e.stopPropagation()}>
                          <OverlayTrigger placement="left" overlay={
                            <Tooltip id={def.tooltipId}>
                              <div style={{ textAlign: 'left' }}>
                                {def.tooltipHeading && (
                                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{def.tooltipHeading}</div>
                                )}
                                <div style={{ marginBottom: '0.5rem' }}>{def.tooltip}</div>
                                <div>{def.tooltipSortedBy}</div>
                              </div>
                            </Tooltip>
                          }>
                            <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer', color: colors.fg, opacity: colors.fg === '#fff' ? 1 : 0.55 }} />
                          </OverlayTrigger>
                        </span>
                      </Card.Header>
                      <Card.Body>
                        <SummaryDashboardSection
                          title=""
                          section={def.sectionForZone}
                          groups={groupBySystem(def.markers, def.scoreOf)}
                          showCharts={expanded}
                          chartsBelow={true}
                        />
                      </Card.Body>
                    </Card>
                  );
                };

                const wrapperCollapsed = isCollapsed(Section.MARKERS_OUTSIDE_OPTIMAL);

                return (
                  <Card className="mb-3">
                    <Card.Header
                      onClick={() => this.toggleSection(Section.MARKERS_OUTSIDE_OPTIMAL)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span>
                        <FontAwesomeIcon icon={wrapperCollapsed ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Markers Outside Optimal&nbsp;
                        <span onClick={e => e.stopPropagation()}>
                          <OverlayTrigger placement="right" overlay={
                            <Tooltip id="markers-outside-optimal-tooltip">
                              <div style={{ textAlign: 'left' }}>
                                <div style={{ marginBottom: '0.5rem' }}>All markers that fall outside the optimal range, grouped by severity (alarm / lab / optimal) and direction (high / low).</div>
                                <div style={{ marginBottom: '0.5rem' }}>Panels on the left are higher priority.</div>
                                <div>Click the arrow in any panel header to hide or reveal the reference-range bar charts beneath each marker.</div>
                              </div>
                            </Tooltip>
                          }>
                            <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer' }} />
                          </OverlayTrigger>
                        </span>
                      </span>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }} onClick={e => e.stopPropagation()}>
                        <label
                          htmlFor="show-bar-graphs-MARKERS_OUTSIDE_OPTIMAL"
                          style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                          title="Show or hide bar graphs in every panel below at once"
                        >
                          <span style={{ marginRight: '6px' }}>Show bar graphs</span>
                          <input
                            id="show-bar-graphs-MARKERS_OUTSIDE_OPTIMAL"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.state.outsideOptimalChartsCollapsed.length === 0}
                            onChange={() => this.toggleAllOutsideOptimalCharts()}
                          />
                        </label>
                        <label
                          htmlFor="include-in-pdf-MARKERS_OUTSIDE_OPTIMAL"
                          style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                          title="Include this section in the Analysis PDF"
                        >
                          <span style={{ marginRight: '6px' }}>Include in PDF</span>
                          <input
                            id="include-in-pdf-MARKERS_OUTSIDE_OPTIMAL"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.isIncludedInPdf(Section.MARKERS_OUTSIDE_OPTIMAL)}
                            onChange={() => this.toggleIncludeInPdf(Section.MARKERS_OUTSIDE_OPTIMAL)}
                          />
                        </label>
                      </div>
                    </Card.Header>
                    {!wrapperCollapsed && (() => {
                      // Two stacked columns mirroring the PDF: highs on the left
                      // (High Alarm → Above Lab → Above Optimal), lows on the right
                      // (Low Alarm → Below Lab → Below Optimal). Each column is its
                      // own flex stack, so panels pack straight up — a shorter side
                      // never forces a gap, and e.g. Above Optimal sits directly
                      // below Above Lab rather than being pinned to a shared row.
                      // renderPanel() returns null for empty zones, so absent panels
                      // simply collapse out of the stack. On narrow viewports the two
                      // columns wrap to subsequent rows.
                      const columns: { key: string; panels: PanelDef[] }[] = [
                        { key: 'moo-highs', panels: [panelDefs[0], panelDefs[1], panelDefs[2]] },
                        { key: 'moo-lows',  panels: [panelDefs[3], panelDefs[4], panelDefs[5]] },
                      ];
                      return (
                        <Card.Body>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px',
                              alignItems: 'flex-start',
                            }}
                          >
                            {columns.map(({ key, panels }) => {
                              if (panels.every(p => p.markers.length === 0)) {
                                return null;
                              }
                              return (
                                <div
                                  key={key}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    flex: '0 0 auto',
                                  }}
                                >
                                  {panels.map(renderPanel)}
                                </div>
                              );
                            })}
                          </div>
                        </Card.Body>
                      );
                    })()}
                  </Card>
                );
              })()}

              <SectionNotesSummaryPanel
                bloodworkAnalysis={bloodworkAnalysis}
                includeInPdf={this.isIncludedInPdf(Section.SECTION_NOTES)}
                onIncludeInPdfToggle={() =>
                  this.toggleIncludeInPdf(Section.SECTION_NOTES)
                }
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <Card id="insights">
                <Card.Header
                  onClick={() => this.toggleSection(Section.NOTES)}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={isCollapsed(Section.NOTES) ? 'chevron-down' : 'chevron-up'} />
                  &nbsp;&nbsp;Insights
                </Card.Header>
                {!isCollapsed(Section.NOTES) && (
                  <Card.Body>
                    <BloodworkNotes
                      section={Section.NOTES}
                      title="Add your own notes here. Click Save when finished."
                      bloodwork={bloodworkAnalysis.bloodwork}
                      bloodworkEntryStore={bloodworkEntryStore}
                      // Save returns to the top of this card, not the top of
                      // the report.
                      scrollAnchorId="insights"
                    />
                  </Card.Body>
                )}
              </Card>

              {supplementsVisible() &&
                hasSupplementProtocolsContent(
                  bloodworkAnalysis,
                  furtherConsiderations,
                  this.props.currentUserProfile.enabledSupplementBrands,
                ) && (
                  <Card className="mb-3" id="supplement-protocols">
                    <Card.Header
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* Title and its info popover travel together on the
                          left, so the Include-in-PDF label sits flush right. */}
                      <span>
                        {/* NAMED FOR THE BRAND, not for one of its views. It
                            used to read "Holobiome Roadmap — Systemic
                            Formulas", which was true when the section was the
                            roadmap; it now holds three views and the roadmap is
                            one of them, so a section-wide title saying
                            "Roadmap" was labelling the whole thing after its
                            third tab. "Holobiome Roadmap" moved to where it
                            belongs — bold at the head of that view's own
                            coverage line (see RoadmapHeadline). */}
                        <span>Systemic Formulas (Supplements formulated by Dr. Shayne Morris)&nbsp;&nbsp;</span>
                        {/* Same shape as the Results Overview legend: wide
                            rather than tall so it can't run off the top of the
                            viewport (Popper can shift a popover, but it can't
                            shrink one that doesn't fit), capped in vw for
                            narrow windows, and scrolling at 70vh instead of
                            clipping. "right-start" aligns its TOP edge with the
                            info icon so it grows downward — plain "right"
                            centers it on the icon, which is what pushed half of
                            this text above the window. */}
                        <OverlayTrigger
                          placement="right-start"
                          trigger="click"
                          rootClose
                          overlay={
                          <Popover
                            id="supplement-protocols-tooltip"
                            style={{ maxWidth: 'min(760px, 92vw)', width: 'max-content' }}
                          >
                            <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                              <div style={{ textAlign: 'left' }}>
                                <p style={POPOVER_BODY}>
                                  <strong>Everything Systemic Formulas is in this one section.</strong> Unchecking Systemic Formulas in Report Settings &rarr; Supplement Settings hides the whole thing. The section has three views, and each has its own <strong>Include in PDF</strong> checkbox above, so a client can be sent the shortlist alone, or the product list without the five-month program, or the program without the list.
                                </p>

                                <div style={POPOVER_HEADING}>Top Priority</div>
                                <p style={POPOVER_BODY}>
                                  What to start with, and the view the section opens on. It is built in two passes. First <strong>coverage</strong>: every system marked Priority or Support gets its own first choice, most concerning system first — the same two tiers the Results Overview calls areas to review. A system&apos;s first choice is the product its own patterns named first, which is the clinical priority; a system whose first choice a worse system already claimed contributes its next one. <strong>Vitamins &amp; Minerals then gets up to two more slots</strong>, because every pattern there is a single marker: one slot cannot carry a client who is at Priority on both vitamin D and magnesium, and a single-marker product can never win a slot on citations. Those extra slots go to that section&apos;s next worst markers and are offered at Priority only, so a panel of mildly low nutrients doesn&apos;t crowd out the systems this list exists to surface. Then <strong>depth</strong>: the slots left over go to the products the most patterns across the whole report point at, from any system, whether or not that system already has one. Citations decide the last pass and never the first, because how widely a product is useful is a different question from how badly this client needs it. The list holds at seven unless Priority findings alone need more, and any system that got no slot is counted in a line under the table.
                                </p>

                                <div style={POPOVER_HEADING}>By System</div>
                                <p style={POPOVER_BODY}>
                                  The same list unabridged. Every Systemic Formulas product this test calls for (kit products and targeted additions together) gets a card per system, worst system first. <strong>Worst system first</strong> is the Results Overview&apos;s own ranking: a system is placed by its most concerning marker (Priority, then Support, then Monitor, then Optimal). Systems sharing a status are ranked by how many of their markers sit outside optimal at any level, so a system with nine markers slightly off sits above one with three in the alarm range. Anything still tied goes on the number of Priority markers, then Support, then Monitor, and a system tied on all of that keeps the report&apos;s own section order. The <strong>Patterns</strong> table at the top of a card names that system&apos;s patterns and numbers them; the <strong>Pattern</strong> column cites those numbers rather than repeating the names. A product several systems asked for is listed under each of them, naming the others beside its pattern number; switch that block to <strong>Once each</strong> to see it filed only under the most concerning system, which is the view for counting bottles. Click a system&apos;s blue header to jump to that section of the report.
                                </p>

                                <div style={POPOVER_HEADING}>Client List</div>
                                <p style={POPOVER_BODY}>
                                  The last column of each card, and what builds the <strong>Recommended Supplements</strong> card below this section: a plain list for the client of product, description and dose, with no patterns or systems on it. <strong>The Top Priority products start ticked and nothing else does</strong>, so the list begins at what to start with and is added to rather than cut down. Ticking follows the product rather than the row, so a product two systems recommend has one box and one line on the list. That card is also where the standard protocol serving for each product is stated, which is why there is no separate dosing table in this section. The list is saved with this test, and it has its own Include in PDF checkbox.
                                </p>

                                <div style={POPOVER_HEADING}>Roadmap</div>
                                <p style={POPOVER_BODY}>
                                  The five kits along their months. Each kit is bought whole, so every product it contains is listed, and the ring over each month shows how many of them this test&apos;s patterns point to. A product that is <strong>highlighted</strong> was identified by a pattern and names the section it came from — click it to jump there. A product marked <strong>not read by blood work</strong> has no marker behind it, so no pattern can ever point to it; it still ships in the kit, and it is left out of the month&apos;s total. A month nothing points to still runs its phase of the protocol, and says so.
                                </p>
                                <p style={{ ...POPOVER_BODY, marginTop: '8px' }}>
                                  <strong>Targeted Additions</strong> under a month are the Systemic Formulas products this test calls for that aren&apos;t in any kit — a separate purchase, scheduled into the month they start. A start month is only ever stated here, on the roadmap: the two list views answer what this test calls for, not when to begin it.
                                </p>
                                <p style={{ ...POPOVER_BODY, marginTop: '8px' }}>
                                  <a
                                    href="https://systemicformulas.com/holobiome-roadmap"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Learn more about the Holobiome Roadmap
                                  </a>
                                </p>
                              </div>
                            </Popover.Content>
                          </Popover>
                          }
                        >
                          <FontAwesomeIcon
                            icon="info-circle"
                            style={{ color: '#fff', cursor: 'pointer' }}
                          />
                        </OverlayTrigger>
                      </span>
                      {/* The roadmap's own PDF keys, not Section.SUPPLEMENTS:
                          the default profile enables Systemic Formulas only, so
                          the Supplements panel — and the checkbox that would
                          otherwise have covered this section too — usually
                          isn't on screen at all.

                          ONE BOX PER VIEW. The three print as separate
                          documents, and a practitioner sending the shortlist
                          alone, or the product list without the five-month
                          program, had no way to say so. Listed in the order the
                          view toggle lists them, so the boxes and the switch
                          below name the three the same way round. */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '12px',
                          color: '#fff',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>Include in PDF</span>
                        <label
                          htmlFor="include-in-pdf-holobiome-top"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            margin: 0,
                            fontWeight: 'normal',
                            cursor: 'pointer',
                          }}
                          title="Include the Top Priority Supplements shortlist in the Analysis PDF"
                        >
                          <span style={{ marginRight: '6px' }}>Top Priority</span>
                          <input
                            id="include-in-pdf-holobiome-top"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.isIncludedInPdf(HOLOBIOME_TOP_SECTION)}
                            onChange={() =>
                              this.toggleIncludeInPdf(HOLOBIOME_TOP_SECTION)
                            }
                          />
                        </label>
                        <label
                          htmlFor="include-in-pdf-holobiome-systems"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            margin: 0,
                            fontWeight: 'normal',
                            cursor: 'pointer',
                          }}
                          title="Include the Pattern-Identified Supplements table in the Analysis PDF"
                        >
                          <span style={{ marginRight: '6px' }}>By System</span>
                          <input
                            id="include-in-pdf-holobiome-systems"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.isIncludedInPdf(HOLOBIOME_SYSTEMS_SECTION)}
                            onChange={() =>
                              this.toggleIncludeInPdf(HOLOBIOME_SYSTEMS_SECTION)
                            }
                          />
                        </label>
                        <label
                          htmlFor="include-in-pdf-holobiome"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            margin: 0,
                            fontWeight: 'normal',
                            cursor: 'pointer',
                          }}
                          title="Include the month-by-month roadmap in the Analysis PDF"
                        >
                          <span style={{ marginRight: '6px' }}>Roadmap</span>
                          <input
                            id="include-in-pdf-holobiome"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.isIncludedInPdf(HOLOBIOME_SECTION)}
                            onChange={() => this.toggleIncludeInPdf(HOLOBIOME_SECTION)}
                          />
                        </label>
                      </span>
                    </Card.Header>
                    <Card.Body>
                      <SupplementProtocolsPanel
                        bloodworkAnalysis={bloodworkAnalysis}
                        furtherConsiderations={furtherConsiderations}
                        enabledSupplementBrands={
                          this.props.currentUserProfile.enabledSupplementBrands
                        }
                        onSectionClick={this.jumpToSection}
                        clientList={clientList}
                      />
                    </Card.Body>
                  </Card>
                )}

              {/* The practitioner's curated list, for the client: name,
                  description and dose, with the patterns and systems left
                  behind in the section above.

                  OUTSIDE the Holobiome card, though it renders directly under
                  it. That card is gated on the Systemic Formulas checkbox, and
                  a client list that disappeared when a brand was unchecked
                  would be the wrong shape for what this is — the one place
                  every brand's ticked products will eventually meet. */}
              {supplementsVisible() && (
                <RecommendedSupplementsPanel
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  enabledSupplementBrands={
                    this.props.currentUserProfile.enabledSupplementBrands
                  }
                  excluded={excludedFromClientList}
                  includeInPdf={this.isIncludedInPdf(
                    Section.RECOMMENDED_SUPPLEMENTS,
                  )}
                  onIncludeInPdfToggle={() =>
                    this.toggleIncludeInPdf(Section.RECOMMENDED_SUPPLEMENTS)
                  }
                  saveState={clientListSaveState}
                  edits={supplementEdits}
                  onEditsChange={this.setSupplementEdits}
                  onIncludeExcluded={this.toggleClientListProduct}
                  severityOrder={rankSectionsBySeverity(bloodworkAnalysis)}
                />
              )}

              {/* Every brand EXCEPT Systemic Formulas, which the Holobiome
                  Roadmap section above owns end to end. The panel stays in
                  place for the brands that aren't SF — BWH Labs today, more
                  later — and renders nothing when none of them are checked. */}
              {supplementsVisible() && (
                <SupplementsSummaryPanel
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  enabledSupplementBrands={
                    this.props.currentUserProfile.enabledSupplementBrands
                  }
                  brandScope="otherBrands"
                  includeInPdf={this.isIncludedInPdf(Section.SUPPLEMENTS)}
                  onIncludeInPdfToggle={() =>
                    this.toggleIncludeInPdf(Section.SUPPLEMENTS)
                  }
                  bloodworkEntryStore={bloodworkEntryStore}
                />
              )}

              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </Card.Body>
          )}
        </Card>

        {/* Room for the jump-down button to put Section Notes / Insights at the
            top of the viewport on a report that ends too close below it. Zero
            until that button needs it, and screen-only — the PDF renders from
            bloodwork-analysis-pdf, not from here. */}
        {jumpSpacerHeight > 0 && (
          <div aria-hidden="true" style={{ height: jumpSpacerHeight }} />
        )}

        <GenericModal
          show={showUpgradeModal}
          title="Export to PDF Feature Not Available"
          text="Export to PDF feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />


        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id="return-to-results-overview-tooltip">
              Return to Results Overview
            </Tooltip>
          }
        >
          <button
            onClick={() => {
              const element = document.getElementById('results-overview');
              if (!element) return;
              const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
              const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
              const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
              window.scrollTo({ top, behavior: 'smooth' });
            }}
            style={{
              // Stacked above the "Jump down to Section Notes" button (44px
              // circle + 8px gap = 52px), keeping the pair anchored bottom-right.
              position: 'fixed',
              bottom: '84px',
              right: '32px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#ff9a9a',
              //766d66 - old taupe circle color; 337ab7 - old blue color
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            <FontAwesomeIcon
              icon={faArrowUp}
              style={{
                fontSize: '18px',
                color: '#fff',
              }}
            />
          </button>
        </OverlayTrigger>

        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id="jump-to-section-notes-tooltip">
              Jump to Section Notes / Insights
            </Tooltip>
          }
        >
          <button
            onClick={this.jumpToSectionNotes}
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#ff9a9a',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            <FontAwesomeIcon
              icon={faArrowDown}
              style={{
                fontSize: '18px',
                color: '#fff',
              }}
            />
          </button>
        </OverlayTrigger>
      </>
    );
  }
}
